import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "./auth/AuthProvider";
import AuthScreen from "./auth/AuthScreen";
import CustomerManager from "./customers/CustomerManager";
import { C, CHART } from "./customers/theme";
import ProductManager from "./products/ProductManager";
import PriceListManager from "./pricelists/PriceListManager";
import CustomerGroupManager from "./customergroups/CustomerGroupManager";
import { DemoDataBanner, DemoModeBanner } from "./components/DemoDataControls";
import AtlasAssistant from "./assistant/AtlasAssistant";
import ComingSoon from "./components/ComingSoon";
import { NavIcon } from "./icons/Icons";
import { useRoute, navigate } from "./lib/router";
import BillingScreen from "./billing/BillingScreen";
import { TrialBanner, ReadOnlyBanner } from "./billing/BillingBanners";
import ProductPicker from "./products/ProductPicker";
import SupplierManager from "./suppliers/SupplierManager";
import PurchaseOrderManager from "./purchaseorders/PurchaseOrderManager";
import GRNManager from "./goodsreceived/GRNManager";
import InventoryManager from "./inventory/InventoryManager";
import SalesOrderManager from "./salesorders/SalesOrderManager";
import DeliveryNoteManager from "./deliverynotes/DeliveryNoteManager";
import QuotationManager from "./quotations/QuotationManager";
import BusinessSettings from "./settings/BusinessSettings";
import OnboardingWizard from "./onboarding/OnboardingWizard";
import { useOnboarding } from "./onboarding/useOnboarding";
import InvoiceTemplateManager from "./settings/InvoiceTemplateManager";
import TeamMembers from "./settings/TeamMembers";
import InvoiceManager from "./invoices/InvoiceManager";
import CreditNoteManager from "./creditnotes/CreditNoteManager";
import StatementManager from "./statements/StatementManager";
import IntelligenceManager from "./intelligence/IntelligenceManager";
import { getInvoiceStats, getRevenueByMonth, getOverdueInvoices } from "./lib/invoices";
import ExpenseManager from "./expenses/ExpenseManager";
import { getExpenses } from "./lib/expenses";
import { listVatReturns, createVatReturn, updateVatReturn, deleteVatReturn, calculateVatReturn, canWriteVat, VAT_RETURN_STATUSES, RETURN_PERIODS } from "./lib/vat";
import { getCustomerScores } from "./lib/customerIntelligence";
import { getProducts } from "./lib/products";
import { listAccounts } from "./lib/chartOfAccounts";
import { localISO, todayISO, parseLocalDate } from "./lib/dates";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

// ─── LOCALSTORAGE PERSISTENCE ────────────────────────────────────────────────
// Each state slice is mirrored to localStorage under "msquared:v1:<key>".
// On change it writes and emits a "msq:saved" event (the app shows a toast).
const STORAGE_PREFIX = "msquared:v1:";

function usePersistentState(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      return raw == null ? initialValue : JSON.parse(raw);
    } catch {
      return initialValue;
    }
  });
  const firstRun = useRef(true);
  useEffect(() => {
    // Don't write (or toast) on the initial mount — only on real changes.
    if (firstRun.current) { firstRun.current = false; return; }
    try {
      localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value));
      window.dispatchEvent(new Event("msq:saved"));
    } catch {
      // storage unavailable or quota exceeded — fail silently
    }
  }, [key, value]);
  return [value, setValue];
}

// Tracks whether the viewport is mobile-sized (drives the responsive layout).
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

// ─── SEED DATA ──────────────────────────────────────────────────────────────

const SEED_CUSTOMERS = [
  { id: "c1", name: "matthew horan", company: "Msquared", email: "matthew@msquared-ai.com", phone: "0834878148", address: "5 roads lusk", county: "Dublin", country: "Ireland", eircode: "K45 K510", status: "active", segment: "new", total_spent: 0, order_count: 0, tags: [], created_date: "2026-06-22" },
  { id: "c2", name: "NARU", company: "NARU", email: "muhammadhairamdiyan@gmail.com", phone: "0894963559", address: "", county: "", country: "Ireland", status: "active", segment: "new", total_spent: 0, order_count: 0, tags: [], notes: "Auto-added via B2B marketplace order", created_date: "2026-06-15" },
  { id: "c3", name: "Seán O'Brien", company: "O'Brien Plumbing Ltd", email: "sean@obrienplumbing.ie", phone: "+353 86 234 5678", address: "45 Shop Street", county: "Galway", country: "Ireland", eircode: "H91 X4F2", vat_number: "IE2345678U", status: "active", segment: "new", total_spent: 0, order_count: 0, created_date: "2026-06-12" },
  { id: "c4", name: "Ciara Walsh", company: "Walsh Catering Co.", email: "ciara@walshcatering.ie", phone: "+353 85 345 6789", address: "8 Patrick Street", county: "Cork", country: "Ireland", eircode: "T12 H8N7", status: "active", segment: "regular", total_spent: 6200, order_count: 1, created_date: "2026-06-12" },
  { id: "c5", name: "Aoife Murphy", company: "Murphy Design Studio", email: "aoife@murphydesign.ie", phone: "+353 87 123 4567", address: "12 Grafton Street", county: "Dublin", country: "Ireland", eircode: "D02 VF25", vat_number: "IE1234567T", status: "active", segment: "regular", total_spent: 4059, order_count: 1, notes: "Long-term client, prefers email communication", created_date: "2026-06-12" },
  { id: "c6", name: "Liam Byrne", company: "Byrne Electrics", email: "liam@byrneelectrics.ie", phone: "+353 89 456 7890", address: "22 O'Connell Street", county: "Limerick", country: "Ireland", eircode: "V94 K5P3", vat_number: "IE3456789V", status: "active", segment: "new", total_spent: 0, order_count: 0, created_date: "2026-06-12" },
  { id: "c7", name: "Niamh Kelly", company: "Kelly's Florist", email: "niamh@kellyflorist.ie", phone: "+353 83 567 8901", address: "3 Market Square", county: "Kilkenny", country: "Ireland", eircode: "R95 A6B2", status: "active", segment: "new", total_spent: 0, order_count: 0, created_date: "2026-06-12" },
];

// Products are loaded live from Supabase via the products data layer (P3-WP4).
// No localStorage product seed/storage remains.


const SEED_ORDERS = [
  { id: "o1", order_number: "ORD-001", customer_id: "c5", customer_name: "Aoife Murphy", status: "completed", subtotal: 3300, vat_amount: 759, total_amount: 4059, due_date: "2025-02-01", created_date: "2026-06-12", items: [{ description: "Website Redesign", quantity: 1, unit_price: 2500, total: 2500 }, { description: "SEO Package", quantity: 1, unit_price: 800, total: 800 }] },
  { id: "o2", order_number: "ORD-002", customer_id: "c3", customer_name: "Seán O'Brien", status: "completed", subtotal: 1250, vat_amount: 287.5, total_amount: 1537.5, created_date: "2026-06-12", items: [{ description: "Emergency Plumbing Supplies", quantity: 50, unit_price: 25, total: 1250 }] },
  { id: "o3", order_number: "ORD-003", customer_id: "c4", customer_name: "Ciara Walsh", status: "completed", subtotal: 6200, vat_amount: 837, total_amount: 7037, due_date: "2025-03-15", created_date: "2026-06-12", items: [{ description: "Wedding Catering Package", quantity: 1, unit_price: 5000, total: 5000 }, { description: "Table Setup & Decoration", quantity: 1, unit_price: 1200, total: 1200 }] },
  { id: "o4", order_number: "ORD-004", customer_id: "c6", customer_name: "Liam Byrne", status: "completed", subtotal: 4500, vat_amount: 607.5, total_amount: 5107.5, created_date: "2026-06-12", items: [{ description: "Full House Rewiring", quantity: 1, unit_price: 4500, total: 4500 }] },
  { id: "o5", order_number: "ORD-005", customer_id: "c2", customer_name: "NARU", status: "completed", subtotal: 3, vat_amount: 0.69, total_amount: 3.69, created_date: "2026-06-15", notes: "Order received via EDI", items: [{ description: "fiji eater", quantity: 1, unit_price: 3, total: 3 }] },
];

// ─── HELPERS ────────────────────────────────────────────────────────────────

const fmt = (n) => `€${(+n || 0).toFixed(2)}`;
const uid = () => Math.random().toString(36).slice(2, 10);
// localISO/todayISO/parseLocalDate are imported from ./lib/dates — see that file's
// header for why they exist. One definition, not two; don't re-add local copies here.

const STATUS_COLORS = {
  active: C.success, inactive: "#94a3b8",
  draft: "#94a3b8", sent: C.primary, paid: C.success,
  overdue: C.danger, cancelled: "#94a3b8", partial: C.warning,
  pending: C.warning, confirmed: C.primary, completed: C.success,
  processing: C.purple, dispatched: "#06b6d4", delivered: C.success,
  new: C.primary, regular: C.purple, vip: C.warning, at_risk: C.danger,
};

const Badge = ({ status }) => (
  <span style={{ background: STATUS_COLORS[status] || "#94a3b8", color: C.white, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap" }}>
    {status?.replace(/_/g, " ")}
  </span>
);

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
// Dark theme: navy/graphite surfaces with teal/neon-blue accents.
// Mirrors the CSS custom properties in index.css so inline styles and CSS stay
// in sync. Prefer referencing C.* over hardcoding hex values.

const FONT_BODY = "'Inter', system-ui, -apple-system, sans-serif";
const FONT_HEAD = "'Space Grotesk', 'Inter', system-ui, sans-serif";

// ─── STYLES ──────────────────────────────────────────────────────────────────

const S = {
  app: { display: "flex", height: "100vh", fontFamily: FONT_BODY, background: C.bg, color: C.text },
  sidebar: { width: 220, background: C.sidebarBg, display: "flex", flexDirection: "column", flexShrink: 0, borderRight: `1px solid ${C.border}` },
  logo: { padding: "16px 20px 12px", borderBottom: `1px solid ${C.border}`, textAlign: "center" },
  logoImg: { width: "100%", maxWidth: 150, height: "auto", display: "block", margin: "0 auto", borderRadius: 10 },
  logoSub: { color: C.textMuted, fontSize: 11, marginTop: 6, letterSpacing: "0.5px" },
  nav: { flex: 1, padding: "12px 0", overflowY: "auto" },
  navSection: { padding: "8px 16px 4px", color: C.textDim, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", cursor: "pointer", borderRadius: 0, color: active ? C.text : C.textMuted, background: active ? C.surface2 : "transparent", fontWeight: active ? 600 : 400, fontSize: 13, transition: "all 0.15s", borderLeft: active ? `3px solid ${C.primary}` : "3px solid transparent" }),
  main: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
  topbar: { background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "12px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" },
  pageTitle: { fontWeight: 700, fontSize: 18, fontFamily: FONT_HEAD, color: C.text },
  content: { flex: 1, overflowY: "auto", padding: 24 },
  card: { background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, padding: 20, marginBottom: 16, overflowX: "auto" },
  statGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16, marginBottom: 24 },
  statCard: (color) => ({ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, padding: 20, borderTop: `3px solid ${color}` }),
  statLabel: { color: C.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" },
  statValue: { fontSize: 28, fontWeight: 800, marginTop: 4, letterSpacing: "-1px", fontFamily: FONT_HEAD, color: C.text },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 12px", background: C.surface2, borderBottom: `1px solid ${C.border}`, fontWeight: 600, color: C.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" },
  td: { padding: "11px 12px", borderBottom: `1px solid ${C.borderSoft}`, verticalAlign: "middle" },
  btn: (variant = "primary") => ({
    padding: "8px 16px", borderRadius: 7, border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13,
    background: variant === "primary" ? C.primary : variant === "danger" ? C.danger : variant === "ghost" ? "transparent" : C.surface2,
    color: variant === "primary" ? C.bg : variant === "danger" ? C.white : variant === "ghost" ? C.textMuted : C.text,
  }),
  input: { width: "100%", padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, boxSizing: "border-box", outline: "none", background: C.surface2, color: C.text },
  label: { display: "block", marginBottom: 4, fontWeight: 600, fontSize: 12, color: C.textMuted },
  formRow: { display: "grid", gap: 16, marginBottom: 16 },
  modal: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100 },
  modalBox: { background: C.surface, color: C.text, borderRadius: 12, padding: 28, width: 580, maxHeight: "85vh", overflowY: "auto", maxWidth: "95vw", border: `1px solid ${C.border}` },
  searchBar: { padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, width: 240, outline: "none", background: C.surface2, color: C.text },
  empty: { textAlign: "center", padding: "48px 0", color: C.textMuted },
  row: { display: "flex", alignItems: "center", gap: 12 },
};

// ─── NAV CONFIG ──────────────────────────────────────────────────────────────
// Single source of truth for the sidebar. Register a completed module here and it
// appears automatically (subject to role). Each item: { id, label, roles? }.
// `roles` is OPTIONAL — omit it for a module visible to every authenticated user;
// set e.g. roles:["owner","admin","accountant"] to restrict (owner/admin always pass).
// ICONS-1: an item's icon is keyed by its `id` in NAV_ICONS (src/icons/Icons.jsx),
// so a new section needs an entry there. src/icons/Icons.test.js fails if it has none.
// Exported for src/MsquaredApp.test.js: LT-1's assertions are about the SHIPPED
// config, not a copy of it re-typed in a test — a test that restates the thing
// it is checking cannot notice the thing changing.
export const NAV = [
  { section: "Overview", items: [{ id: "dashboard", label: "Dashboard" }] },
  { section: "Sales", items: [{ id: "customers", label: "Customers" }, { id: "customergroups", label: "Customer Groups" }, { id: "intelligence", label: "Customer Intelligence" }, { id: "salesorders", label: "Sales Orders" }, { id: "deliverynotes", label: "Delivery Notes" }, { id: "invoices", label: "Invoices" }, { id: "creditnotes", label: "Credit Notes" }, { id: "statements", label: "Statements" }, { id: "quotations", label: "Quotations" }] },
  { section: "Catalogue", items: [{ id: "products", label: "Products" }, { id: "pricelists", label: "Price Lists" }, { id: "suppliers", label: "Suppliers" }, { id: "inventory", label: "Inventory" }] },
  { section: "Purchasing", items: [{ id: "purchaseorders", label: "Purchase Orders" }, { id: "grn", label: "Goods Received" }] },
  { section: "Finance", items: [{ id: "expenses", label: "Expenses" }, { id: "accounts", label: "Chart of Accounts", roles: ["owner","admin","accountant"] }, { id: "vat", label: "VAT Returns", roles: ["owner","admin","accountant"] }] },
  { section: "Settings", items: [{ id: "settings", label: "Business Settings", roles: ["owner","admin"] }, { id: "invoicetemplates", label: "Invoice Templates", roles: ["owner","admin"] }, { id: "team", label: "Team Members", roles: ["owner","admin"] }, { id: "billing", label: "Billing", roles: ["owner"] }] },
  // ── LT-1 — THE SIX BROWSER-ONLY PREVIEWS, GATHERED AND COLLAPSED ─────────
  // They were scattered through Finance, Operations and Accounting, reading as
  // finished features beside real ones. Each saved to ONE browser's
  // localStorage: not on the user's account, not backed up, not shared with
  // their team, gone when they cleared their browser or picked up a different
  // laptop. BrowserOnlyBanner said so at the top of each, and a labelled
  // preview is still a screen a customer can pour a month of real work into
  // and lose.
  //
  // They stay VISIBLE, greyed and collapsed, rather than being deleted from
  // the nav: a customer who used one needs to find out where it went, and
  // "Coming soon" answers that where a silently missing menu item does not.
  // NO `roles` — a Coming-soon screen has nothing to protect, and gating it
  // would tell a driver the feature does not exist rather than that it is not
  // ready. `orders` is NOT here: it is GONE, because Sales Orders is the real,
  // database-backed version of the same thing and two "orders" in one nav is
  // a support question, not a feature.
  { section: "Coming soon", collapsed: true, items: [
    { id: "bank", label: "Bank" },
    { id: "payroll", label: "Payroll" },
    { id: "staff", label: "Staff & Rota" },
    { id: "projects", label: "Projects" },
    { id: "calendar", label: "Calendar" },
    { id: "acctasks", label: "Accounting Tasks" },
  ] },
];

// LT-1: the ids that render ComingSoon. Kept next to NAV so the two cannot
// drift — a screen added to the section above and forgotten here would render
// a blank page.
export const COMING_SOON_PAGES = ["bank", "payroll", "staff", "projects", "calendar", "acctasks"];

// ─── MODAL WRAPPER ───────────────────────────────────────────────────────────

function Modal({ title, onClose, children }) {
  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.modalBox}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{title}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8" }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── BROWSER-ONLY PREVIEW BANNER ─────────────────────────────────────────────
// Shared by every page still backed by usePersistentState (localStorage), never
// Supabase — the seven render sites are Orders, Bank, Payroll, Staff & Rota,
// Calendar, Projects, Accounting Tasks (P4-CC17: docs/roadmap/CC17-pre-beta-
// hardening.md, whose §1 table is kept current and is the authority). ONE
// component, one place to change the wording — never duplicate this text into
// an individual page. `legal` adds the second sentence for the pages that hold
// records a business is legally required to keep (Bank, Payroll). Deliberately
// not dismissible — a "don't show again" would defeat the point the day it
// stops being remembered.
//
// ⛔ VAT RETURNS IS OFF BOTH LISTS (P4-CC26d-0-fix4). It was migrated to a real
// typed table by P4-CC18 and `case "vat"` has rendered no banner since — this
// comment simply never caught up, which is how the list came to be trusted for
// a count it could not support.
//
// ⛔ CHART OF ACCOUNTS IS OFF BOTH LISTS (P4-CC26d-0-fix3). This banner told
// that page's user it "saves to this browser only — not saved to your account,
// not backed up, and not shared with your team". After P4-CC26d-0 every clause
// of that is false: the chart reads public.accounts from Supabase through
// src/lib/chartOfAccounts.js, and the Journal Entries tab stores nothing at all.
// The `legal` line was worse than merely wrong. It told a user that records
// their business is legally required to keep were sitting unbacked-up in a
// browser, when they are in Postgres, RLS'd and backed up — so it pushed them
// toward keeping a duplicate set somewhere else, which for statutory records is
// the expensive kind of wrong. A false storage warning on a finance screen
// costs the same trust as a missing one.
function BrowserOnlyBanner({ legal = false }) {
  return (
    <div role="note" style={{ display: "flex", gap: 10, alignItems: "flex-start", background: "#2b2410", border: `1px solid ${C.warning}`, borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: C.text, lineHeight: 1.55 }}>
      <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
      <div>
        <strong>Preview feature</strong> — this page saves to <strong>this browser</strong> only. It is not saved to your account, not backed up, and not shared with your team. Clearing your browser data will delete it, and you won't see it on any other device or browser. Keep anything you need long-term in your existing system.
        {legal && (
          <div style={{ marginTop: 6, fontWeight: 600 }}>
            These are records your business is legally required to keep — this page must not be the only place they exist.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────

// `page` does double duty: it is the route renderPage() must handle AND the
// NAV_ICONS key, so the button shows the same M² icon as that page's nav row.
// There is no `icon` field — the emoji it held (🧾 📦 👥 💸) are gone.
//
// ⛔ EXPORTED so MsquaredApp.test.js can assert on the SHIPPED array rather than a
// re-typed copy, the same reason NAV and COMING_SOON_PAGES are. The guard that
// matters: every `page` here must be a case in renderPage(). "New Order" once
// pointed at "orders", which no case matched, so the button rendered a blank
// screen (fixed in 3745064).
export const QUICK_ACTIONS = [
  { label: "New Invoice", page: "invoices", color: C.primary },
  { label: "New Order", page: "salesorders", color: C.success },
  { label: "New Customer", page: "customers", color: C.purple },
  { label: "Log Expense", page: "expenses", color: C.warning },
];

function Dashboard({ onQuickAction, onOpenInvoice }) {
  const now = new Date();
  const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const thisMonth = monthKey(now);
  const [revMonths, setRevMonths] = useState(6);
  const [expMonths, setExpMonths] = useState(6);

  // ── Live invoice figures (CC2 — real Supabase invoices, not seed) ───────────
  const EMPTY_STATS = { paidTotal: 0, outstanding: 0, overdueCount: 0, paidThisMonth: 0 };
  const [invStats, setInvStats] = useState(EMPTY_STATS);
  const [paidByMonth, setPaidByMonth] = useState({});   // "YYYY-MM" → paid revenue (6 mo, for the net sparkline)
  const [overdueList, setOverdueList] = useState([]);
  const [revenueLive, setRevenueLive] = useState([]);
  const [expenses, setExpenses] = useState([]);   // CC3 — live expenses (was a seed prop)
  // P4-CC19a — getExpenses({ pageSize: 1000 }) below is capped: a company with
  // more than 1,000 expenses gets a SILENTLY short `expenses` array feeding four
  // reduces (category pie, this-month Money Out, the 6-month net sparkline, the
  // expenses trend chart) plus the CSV export. { count: "exact" } already tells
  // us the TRUE total regardless of the page we fetched — no extra "+1" request
  // needed here, unlike statements.js's loud-failure pattern, since getExpenses()
  // already asks PostgREST for the exact count alongside the capped page. We do
  // NOT throw on this (a dashboard that fails to load on this is worse than a
  // document that refuses to print — see the CC19a instructions): the screen
  // stays up with the affected figures visibly flagged, and only the CSV export
  // (an artifact that leaves the building) is refused outright.
  const [expensesTruncated, setExpensesTruncated] = useState(false);
  const [custCount, setCustCount] = useState(0);  // live customer total (Supabase — reconciles with Customer Intelligence)
  const [custRows, setCustRows] = useState([]);   // live customer rows (company_name + lifetime_value)
  const [invLoading, setInvLoading] = useState(true);
  const [invError, setInvError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setInvLoading(true); setInvError("");
      try {
        const [stats, rev6, overdue, exp, scores] = await Promise.all([getInvoiceStats(), getRevenueByMonth(6), getOverdueInvoices(5), getExpenses({ pageSize: 1000 }), getCustomerScores()]);
        if (!alive) return;
        setInvStats(stats); setOverdueList(overdue); setExpenses(exp.data || []);
        setExpensesTruncated((exp.count ?? 0) > (exp.data || []).length);
        setPaidByMonth(Object.fromEntries(rev6.map((r) => [r.key, r.revenue])));
        setCustCount(scores.count || 0); setCustRows(scores.data || []);
      } catch (e) { if (alive) { setInvError(e.message || "Could not load live figures."); setInvStats(EMPTY_STATS); setOverdueList([]); setPaidByMonth({}); setExpenses([]); setExpensesTruncated(false); setCustCount(0); setCustRows([]); } }
      finally { if (alive) setInvLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    let alive = true;
    (async () => { try { const r = await getRevenueByMonth(revMonths); if (alive) setRevenueLive(r); } catch { if (alive) setRevenueLive([]); } })();
    return () => { alive = false; };
  }, [revMonths]);

  const totalRevenue = invStats.paidTotal || 0;
  const outstanding = invStats.outstanding || 0;
  const overdueCount = invStats.overdueCount || 0;

  // Revenue chart — paid invoices by issue month, following the M selector.
  const monthLabel = (key) => { const [y, mm] = key.split("-"); const d = new Date(+y, +mm - 1, 1); return d.toLocaleDateString("en-IE", revMonths > 6 ? { month: "short", year: "2-digit" } : { month: "short" }); };
  const revenueData = revenueLive.map((r) => ({ name: monthLabel(r.key), Revenue: Math.round(r.revenue) }));

  // ── Expenses by category (pie) ─────────────────────────────────────────────
  const catMap = {};
  expenses.forEach(e => { const c = e.category || "other"; catMap[c] = (catMap[c] || 0) + (e.total_amount || 0); });
  const pieData = Object.entries(catMap)
    .map(([name, value]) => ({ name: name.replace(/_/g, " "), value: Math.round(value * 100) / 100 }))
    .sort((a, b) => b.value - a.value);

  // ── Cashflow this month — Invoices vs Expenses, both Supabase-backed ────────
  // P4-CC17: Orders and Bank credits/debits were removed from these sums —
  // both are usePersistentState (browser-only, never backed up) — because
  // blending them into a figure labelled "Money In"/"Net" made a real-looking
  // total partly unreconcilable: the customer had no way to see, edit or
  // export the localStorage component that was silently part of it. This is
  // now a complete, honest picture of exactly what it's named for: invoices
  // issued and paid, and expenses logged, both in Supabase. See
  // docs/roadmap/CC17-pre-beta-hardening.md.
  const inInvoices = invStats.paidThisMonth || 0;
  const moneyIn = inInvoices;
  const outExpenses = expenses.filter(e => (e.expense_date || "").slice(0, 7) === thisMonth).reduce((s, e) => s + (e.total_amount || 0), 0);
  const moneyOut = outExpenses;
  const net = moneyIn - moneyOut;
  const flowMax = Math.max(moneyIn, moneyOut, 1);

  // Per-month net cashflow (for the Net sparkline) and expenses trend. Same
  // Invoices-vs-Expenses scope as above, for all 6 months — a sparkline that
  // still included Orders/Bank while the headline figures didn't would stop
  // agreeing with them, which is its own reconciliation trap.
  const monthlyTotals = (key) => {
    const inv = paidByMonth[key] || 0; // LIVE paid invoices for the month
    const ex = expenses.filter(e => (e.expense_date || "").slice(0, 7) === key).reduce((s, e) => s + (e.total_amount || 0), 0);
    return { inflow: inv, outflow: ex };
  };
  const netSeries = [];
  for (let k = 5; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const { inflow, outflow } = monthlyTotals(monthKey(d));
    netSeries.push({ name: d.toLocaleDateString("en-IE", { month: "short" }), net: Math.round(inflow - outflow) });
  }
  const netTrendUp = (netSeries[netSeries.length - 1]?.net || 0) >= 0;

  // Expenses over time (own range).
  const expTrend = [];
  for (let k = expMonths - 1; k >= 0; k--) {
    const d = new Date(now.getFullYear(), now.getMonth() - k, 1);
    const label = expMonths > 6 ? d.toLocaleDateString("en-IE", { month: "short", year: "2-digit" }) : d.toLocaleDateString("en-IE", { month: "short" });
    const total = expenses.filter(e => (e.expense_date || "").slice(0, 7) === monthKey(d)).reduce((s, e) => s + (e.total_amount || 0), 0);
    expTrend.push({ name: label, Expenses: Math.round(total) });
  }

  // ── CSV export of the dashboard figures ────────────────────────────────────
  // P4-CC19a — unlike the on-screen figures, this file can leave the building
  // (forwarded to an accountant, etc.), so it gets the stricter, document-style
  // treatment: refuse outright rather than bake in an understated number.
  const exportCSV = () => {
    if (expensesTruncated) {
      window.alert("Cannot export — this company has more expenses than can be loaded here (over 1,000), so the expense figures would be incomplete. Contact support before exporting.");
      return;
    }
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [];
    rows.push(["Msquared Dashboard Export", now.toLocaleDateString("en-IE")]);
    rows.push([]);
    rows.push(["Summary"]);
    rows.push(["Metric", "Value (EUR)"]);
    rows.push(["Customers", custCount]);
    rows.push(["Revenue (Paid)", totalRevenue.toFixed(2)]);
    rows.push(["Outstanding", outstanding.toFixed(2)]);
    rows.push(["Overdue Invoices", overdueCount]);
    rows.push([]);
    rows.push([`Cashflow — Invoices & Expenses (${now.toLocaleDateString("en-IE", { month: "long", year: "numeric" })})`]);
    rows.push(["Source", "Amount (EUR)"]);
    rows.push(["Money In — Invoices", inInvoices.toFixed(2)]);
    rows.push(["Money In — Total", moneyIn.toFixed(2)]);
    rows.push(["Money Out — Expenses", outExpenses.toFixed(2)]);
    rows.push(["Money Out — Total", moneyOut.toFixed(2)]);
    rows.push(["Net", net.toFixed(2)]);
    rows.push([]);
    rows.push([`Revenue — last ${revMonths} months`]);
    rows.push(["Month", "Revenue (EUR)"]);
    revenueData.forEach(r => rows.push([r.name, r.Revenue]));
    rows.push([]);
    rows.push(["Expenses by Category"]);
    rows.push(["Category", "Amount (EUR)"]);
    if (pieData.length === 0) rows.push(["(none)", "0.00"]);
    pieData.forEach(p => rows.push([p.name, p.value.toFixed(2)]));
    rows.push([]);
    rows.push(["Top 5 Overdue Invoices"]);
    rows.push(["Invoice", "Customer", "Amount (EUR)", "Days Overdue"]);
    overdueList.forEach(o => rows.push([o.invoice_number, o.customer_name, (o.total_amount || 0).toFixed(2), o.days_overdue]));

    const csv = rows.map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `msquared-dashboard-${localISO(now)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {invError && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>⚠ {invError}</span><button style={S.btn("secondary")} onClick={() => setInvError("")}>Dismiss</button>
        </div>
      )}
      {invLoading && <div style={{ color: "#64748b", fontSize: 12, marginBottom: 8 }}>Loading live invoice figures…</div>}
      {expensesTruncated && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 10, background: "#3a2f1a", border: `1px solid ${C.warning}`, color: C.text, borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>
          <span style={{ fontWeight: 600 }}>⚠ This company has more than 1,000 expenses — Money Out, Net, and the expense charts below are based on an incomplete set and may be understated. Export CSV is disabled until this is resolved. Contact support.</span>
        </div>
      )}
      {/* Stat cards */}
      <div style={S.statGrid}>
        <div style={S.statCard(C.primary)}><div style={S.statLabel}>Customers</div><div style={S.statValue}>{custCount}</div></div>
        <div style={S.statCard(C.success)}><div style={S.statLabel}>Revenue (Paid)</div><div style={S.statValue}>{fmt(totalRevenue)}</div></div>
        <div style={S.statCard(C.warning)}><div style={S.statLabel}>Outstanding</div><div style={S.statValue}>{fmt(outstanding)}</div></div>
        <div style={S.statCard(C.danger)}><div style={S.statLabel}>Overdue Invoices</div><div style={S.statValue}>{overdueCount}</div></div>
      </div>

      {/* Quick Actions */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Quick Actions</h3>
          <button
            onClick={exportCSV}
            disabled={expensesTruncated}
            style={{ ...S.btn("secondary"), fontSize: 12, ...(expensesTruncated ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
            title={expensesTruncated ? "Export disabled — expense figures are incomplete (over 1,000 expenses). Contact support." : "Download the dashboard figures as a CSV file"}
          >⬇ Export CSV</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {QUICK_ACTIONS.map(a => (
            <button key={a.page} onClick={() => onQuickAction && onQuickAction(a.page)}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "14px 10px", borderRadius: 9, border: `1px solid ${a.color}33`, background: `${a.color}0d`, color: a.color, fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
              {/* accent={false}: the button already sets its own colour from a.color, and
                  the icon's accent colour would fight with it. Without the accent the
                  accent paths fall back to currentColor, so the icon inherits the
                  button's colour exactly as the emoji did. */}
              <NavIcon id={a.page} size={18} accent={false} /> {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cashflow this month — Invoices vs Expenses only; see the P4-CC17 comment above */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 14px", fontWeight: 700 }}>Cashflow — Invoices &amp; Expenses — {now.toLocaleDateString("en-IE", { month: "long", year: "numeric" })}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 16 }}>
          <div>
            <div style={S.statLabel}>Money In</div>
            <div style={{ ...S.statValue, color: C.success, fontSize: 24 }}>{fmt(moneyIn)}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, lineHeight: 1.6 }}>
              Invoices {fmt(inInvoices)}
            </div>
          </div>
          <div>
            <div style={S.statLabel}>Money Out</div>
            <div style={{ ...S.statValue, color: C.danger, fontSize: 24 }}>{fmt(moneyOut)}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, lineHeight: 1.6 }}>
              Expenses {fmt(outExpenses)}
            </div>
          </div>
          <div>
            <div style={S.statLabel}>Net</div>
            <div style={{ ...S.statValue, color: net >= 0 ? C.success : C.danger, fontSize: 24 }}>{net >= 0 ? "+" : "−"}{fmt(Math.abs(net))}</div>
            <div style={{ height: 36, marginTop: 6 }} title="Net cashflow, last 6 months">
              <ResponsiveContainer width="100%" height={36}>
                <LineChart data={netSeries} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                  <Tooltip formatter={(v) => fmt(v)} labelFormatter={() => ""} contentStyle={{ borderRadius: 6, border: `1px solid ${C.border}`, fontSize: 12, padding: "2px 8px", background: C.surface, color: C.text }} itemStyle={{ color: C.text }} />
                  <Line type="monotone" dataKey="net" stroke={netTrendUp ? C.success : C.danger} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 70, fontSize: 12, color: "#64748b" }}>In</span>
            <div style={{ flex: 1, background: C.surface2, borderRadius: 5, height: 14, overflow: "hidden" }}><div style={{ width: `${(moneyIn / flowMax) * 100}%`, background: C.success, height: "100%" }} /></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ width: 70, fontSize: 12, color: "#64748b" }}>Out</span>
            <div style={{ flex: 1, background: C.surface2, borderRadius: 5, height: 14, overflow: "hidden" }}><div style={{ width: `${(moneyOut / flowMax) * 100}%`, background: C.danger, height: "100%" }} /></div>
          </div>
        </div>
      </div>

      {/* Revenue line chart */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Revenue — last {revMonths} months (paid invoices)</h3>
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 7, overflow: "hidden" }}>
            {[3, 6, 12].map(m => (
              <button key={m} onClick={() => setRevMonths(m)}
                style={{ padding: "5px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: revMonths === m ? C.primary : C.surface2, color: revMonths === m ? C.bg : C.textMuted }}>
                {m}M
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={revenueData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.surface2} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: C.border }} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000) + "k" : v}`} axisLine={false} tickLine={false} width={56} />
            <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text }} itemStyle={{ color: C.text }} labelStyle={{ color: C.textMuted }} />
            <Line type="monotone" dataKey="Revenue" stroke={C.primary} strokeWidth={2.5} dot={{ r: 3, fill: C.primary }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Expenses over time */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontWeight: 700 }}>Expenses — last {expMonths} months</h3>
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 7, overflow: "hidden" }}>
            {[3, 6, 12].map(m => (
              <button key={m} onClick={() => setExpMonths(m)}
                style={{ padding: "5px 14px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: expMonths === m ? C.warning : C.surface2, color: expMonths === m ? C.bg : C.textMuted }}>
                {m}M
              </button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={expTrend} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.surface2} vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={{ stroke: C.border }} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: "#64748b" }} tickFormatter={(v) => `€${v >= 1000 ? (v / 1000) + "k" : v}`} axisLine={false} tickLine={false} width={56} />
            <Tooltip formatter={(v) => fmt(v)} cursor={{ fill: C.surface }} contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text }} itemStyle={{ color: C.text }} labelStyle={{ color: C.textMuted }} />
            <Bar dataKey="Expenses" fill={C.warning} radius={[4, 4, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expenses pie + Top overdue */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div style={S.card}>
          <h3 style={{ margin: "0 0 12px", fontWeight: 700 }}>Expenses by Category</h3>
          {pieData.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 13 }}>
              No expenses logged yet.<br />Use “Log Expense” above to add one.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} innerRadius={45} paddingAngle={2}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: 11 }}>
                  {pieData.map((e, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: C.surface, color: C.text }} itemStyle={{ color: C.text }} labelStyle={{ color: C.textMuted }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={S.card}>
          <h3 style={{ margin: "0 0 12px", fontWeight: 700 }}>Top 5 Overdue Invoices</h3>
          {overdueList.length === 0 ? (
            <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 13 }}>No overdue invoices. 🎉</div>
          ) : (
            <table style={S.table}>
              <thead><tr><th style={S.th}>Invoice</th><th style={S.th}>Customer</th><th style={S.th}>Amount</th><th style={S.th}>Overdue</th></tr></thead>
              <tbody>{overdueList.map(inv => (
                <tr key={inv.id} style={{ cursor: onOpenInvoice ? "pointer" : "default" }} onClick={() => onOpenInvoice && onOpenInvoice(inv.id)} title="Open this invoice">
                  <td style={S.td}><strong>{inv.invoice_number}</strong></td>
                  <td style={S.td}>{inv.customer_name}</td>
                  <td style={S.td}><strong>{fmt(inv.total_amount)}</strong></td>
                  <td style={S.td}><span style={{ color: C.danger, fontWeight: 700 }}>{inv.days_overdue} day{inv.days_overdue === 1 ? "" : "s"}</span></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>

      {/* Top customers */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 12px", fontWeight: 700 }}>Top Customers</h3>
        {[...custRows].sort((a, b) => (b.lifetime_value || 0) - (a.lifetime_value || 0)).filter(c => (c.lifetime_value || 0) > 0).slice(0, 5).map(c => (
          <div key={c.id} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.surface2}` }}>
            <div style={{ fontWeight: 600 }}>{c.company_name}</div>
            <div style={{ fontWeight: 700 }}>{fmt(c.lifetime_value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── ORDERS ──────────────────────────────────────────────────────────────────

function Orders({ orders, setOrders, customers, products = [], autoNew, onAutoNew }) {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const filtered = orders.filter(o => `${o.order_number || ""} ${o.customer_name}`.toLowerCase().includes(search.toLowerCase()));

  const openNew = () => {
    setForm({ status: "pending", order_number: `ORD-${uid().toUpperCase().slice(0, 6)}`, created_date: todayISO(), items: [{ description: "", quantity: 1, unit_price: 0, total: 0 }], subtotal: 0, vat_amount: 0, total_amount: 0 });
    setModal("edit");
  };
  useEffect(() => {
    if (autoNew) { openNew(); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const calcTotals = (items) => {
    const subtotal = items.reduce((s, i) => s + ((i.quantity || 0) * (i.unit_price || 0)), 0);
    const vat_amount = subtotal * 0.23;
    return { ...form, items, subtotal, vat_amount, total_amount: subtotal + vat_amount };
  };

  const save = () => {
    if (!form.id) setOrders(prev => [{ ...form, id: uid() }, ...prev]);
    else setOrders(prev => prev.map(o => o.id === form.id ? form : o));
    setModal(null);
  };

  const del = (id) => { if (window.confirm("Delete this order?")) setOrders(prev => prev.filter(o => o.id !== id)); };

  const STATUS_OPTIONS = ["pending", "confirmed", "processing", "packed", "dispatched", "delivered", "completed", "cancelled"];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <input style={S.searchBar} placeholder="Search orders..." value={search} onChange={e => setSearch(e.target.value)} />
        <button style={S.btn()} onClick={openNew}>+ New Order</button>
      </div>
      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Order</th><th style={S.th}>Customer</th><th style={S.th}>Date</th><th style={S.th}>Items</th><th style={S.th}>Total</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
          <tbody>{filtered.length === 0 ? <tr><td colSpan={7} style={S.empty}>No orders found</td></tr> : filtered.map(o => (
            <tr key={o.id} style={{ cursor: "pointer" }} onClick={() => { setForm({ ...o }); setModal("edit"); }}>
              <td style={S.td}><strong>{o.order_number || "—"}</strong></td>
              <td style={S.td}>{o.customer_name}</td>
              <td style={S.td}>{o.created_date}</td>
              <td style={S.td}>{o.items?.length || 0} item{o.items?.length !== 1 ? "s" : ""}</td>
              <td style={S.td}><strong>{fmt(o.total_amount)}</strong></td>
              <td style={S.td}><Badge status={o.status} /></td>
              <td style={S.td} onClick={e => { e.stopPropagation(); del(o.id); }}><button style={S.btn("ghost")}>🗑</button></td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      {modal === "edit" && (
        <Modal title={form.id ? `Order ${form.order_number}` : "New Order"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><label style={S.label}>Order Number</label><input style={S.input} value={form.order_number || ""} onChange={e => setForm(p => ({ ...p, order_number: e.target.value }))} /></div>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Customer</label>
              <select style={S.input} value={form.customer_id || ""} onChange={e => { const c = customers.find(c => c.id === e.target.value); if (c) setForm(p => ({ ...p, customer_id: c.id, customer_name: c.name })); }}>
                <option value="">Select customer...</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} — {c.company}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            {(form.items || []).map((item, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <ProductPicker products={products} style={{ marginBottom: 6, width: "100%" }} onSelect={(p) => {
                  const it = [...form.items];
                  it[i] = { ...it[i], description: p.name, unit_price: p.wholesale_price ?? it[i].unit_price };
                  setForm(calcTotals(it));
                }} />
                <div style={{ display: "grid", gridTemplateColumns: "2fr 80px 100px 36px", gap: 8 }}>
                  <input style={S.input} value={item.description} onChange={e => { const it = [...form.items]; it[i] = { ...it[i], description: e.target.value }; setForm(calcTotals(it)); }} placeholder="Description" />
                  <input style={S.input} type="number" value={item.quantity} onChange={e => { const it = [...form.items]; it[i] = { ...it[i], quantity: +e.target.value }; setForm(calcTotals(it)); }} />
                  <input style={S.input} type="number" value={item.unit_price} onChange={e => { const it = [...form.items]; it[i] = { ...it[i], unit_price: +e.target.value }; setForm(calcTotals(it)); }} placeholder="Price" />
                  <button style={S.btn("ghost")} onClick={() => { const it = form.items.filter((_, j) => j !== i); setForm(calcTotals(it)); }}>✕</button>
                </div>
              </div>
            ))}
            <button style={S.btn("secondary")} onClick={() => setForm(p => ({ ...p, items: [...(p.items || []), { description: "", quantity: 1, unit_price: 0, total: 0 }] }))}>+ Add Line</button>
          </div>
          <div style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span>Subtotal</span><strong>{fmt(form.subtotal)}</strong></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800 }}><span>Total (incl. VAT)</span><span>{fmt(form.total_amount)}</span></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={save}>Save Order</button>
          </div>
        </Modal>
      )}
    </div>
  );
}


// ─── ACCOUNTING TASKS ────────────────────────────────────────────────────────

const TASK_CATEGORIES = ["bookkeeping","vat_return","payroll","annual_accounts","tax_filing","audit","reconciliation","other"];
const TASK_STATUSES   = ["todo","in_progress","review","done"];
const TASK_PRIORITIES = ["low","medium","high","urgent"];

const PRIORITY_COLOR = { low: "#94a3b8", medium: C.primary, high: C.warning, urgent: C.danger };
const STATUS_LABEL   = { todo: "To Do", in_progress: "In Progress", review: "Review", done: "Done" };
const STATUS_BG      = { todo: C.surface2, in_progress: C.primaryDim, review: C.warningDim, done: C.successDim };
const STATUS_BORDER  = { todo: C.border, in_progress: "#115160", review: "#6b5413", done: "#1c5234" };

function AccountingTasks({ tasks, setTasks, customers }) {
  const [view, setView]   = useState("kanban"); // "kanban" | "list"
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState({});
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("all");

  const filtered = tasks.filter(t => {
    const matchCat  = filterCat === "all" || t.category === filterCat;
    const matchSearch = `${t.title} ${t.client_name || ""} ${t.description || ""}`.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const openNew = () => {
    setForm({ status: "todo", priority: "medium", category: "other", is_recurring: false, checklist: [] });
    setModal("edit");
  };

  const save = () => {
    if (!form.id) setTasks(prev => [{ ...form, id: uid() }, ...prev]);
    else setTasks(prev => prev.map(t => t.id === form.id ? form : t));
    setModal(null);
  };

  const del = (id) => { if (window.confirm("Delete this task?")) setTasks(prev => prev.filter(t => t.id !== id)); };

  const moveStatus = (task, newStatus) => setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));

  const addCheckItem = () => setForm(p => ({ ...p, checklist: [...(p.checklist || []), { label: "", done: false }] }));
  const removeCheckItem = (i) => setForm(p => ({ ...p, checklist: p.checklist.filter((_, j) => j !== i) }));
  const updateCheckLabel = (i, label) => setForm(p => ({ ...p, checklist: p.checklist.map((c, j) => j === i ? { ...c, label } : c) }));

  const isOverdue = (t) => t.due_date && t.status !== "done" && new Date(t.due_date) < new Date();

  const KanbanCol = ({ status }) => {
    const col = filtered.filter(t => t.status === status);
    return (
      <div style={{ flex: 1, minWidth: 220, background: STATUS_BG[status], border: `1px solid ${STATUS_BORDER[status]}`, borderRadius: 10, padding: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>{STATUS_LABEL[status]}</span>
          <span style={{ background: C.surface2, borderRadius: 12, padding: "1px 8px", fontSize: 11, color: C.textMuted }}>{col.length}</span>
        </div>
        {col.length === 0 && <div style={{ color: "#94a3b8", fontSize: 12, textAlign: "center", padding: "16px 0" }}>No tasks</div>}
        {col.map(task => (
          <div key={task.id} style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 8, border: `1px solid ${isOverdue(task) ? "#5c2a2a" : C.border}`, cursor: "pointer" }}
            onClick={() => { setForm({ ...task }); setModal("edit"); }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <div style={{ fontWeight: 600, fontSize: 13, flex: 1, marginRight: 6 }}>{task.title}</div>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: PRIORITY_COLOR[task.priority], flexShrink: 0, marginTop: 4 }} title={task.priority} />
            </div>
            {task.client_name && <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>👤 {task.client_name}</div>}
            {task.due_date && <div style={{ fontSize: 11, color: isOverdue(task) ? C.danger : "#94a3b8", marginBottom: 4 }}>📅 {task.due_date}{isOverdue(task) ? " · OVERDUE" : ""}</div>}
            {task.category && <div style={{ fontSize: 10, background: C.surface2, borderRadius: 10, padding: "2px 8px", display: "inline-block", marginBottom: 6, color: "#475569", textTransform: "uppercase", letterSpacing: "0.04em" }}>{task.category.replace(/_/g, " ")}</div>}
            {task.checklist?.length > 0 && (
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 6 }}>
                ☑ {task.checklist.filter(c => c.done).length}/{task.checklist.length} done
                <div style={{ background: C.border, borderRadius: 4, height: 4, marginTop: 3 }}>
                  <div style={{ background: C.success, borderRadius: 4, height: 4, width: `${task.checklist.length ? (task.checklist.filter(c => c.done).length / task.checklist.length) * 100 : 0}%` }} />
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 4, marginTop: 6 }} onClick={e => e.stopPropagation()}>
              {TASK_STATUSES.filter(s => s !== status).map(s => (
                <button key={s} style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 7px", border: `1px solid ${C.border}` }} onClick={() => moveStatus(task, s)}>
                  → {STATUS_LABEL[s]}
                </button>
              ))}
              <button style={{ ...S.btn("ghost"), fontSize: 10, padding: "2px 7px", color: C.danger }} onClick={() => del(task.id)}>🗑</button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <input style={S.searchBar} placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...S.input, width: "auto" }} value={filterCat} onChange={e => setFilterCat(e.target.value)}>
            <option value="all">All categories</option>
            {TASK_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 7, overflow: "hidden" }}>
            {["kanban","list"].map(v => (
              <button key={v} style={{ padding: "7px 14px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: view === v ? C.primary : C.surface2, color: view === v ? C.bg : C.textMuted }} onClick={() => setView(v)}>
                {v === "kanban" ? "⬛ Board" : "☰ List"}
              </button>
            ))}
          </div>
        </div>
        <button style={S.btn()} onClick={openNew}>+ New Task</button>
      </div>

      <div style={S.statGrid}>
        <div style={S.statCard("#94a3b8")}><div style={S.statLabel}>To Do</div><div style={S.statValue}>{tasks.filter(t => t.status === "todo").length}</div></div>
        <div style={S.statCard(C.primary)}><div style={S.statLabel}>In Progress</div><div style={S.statValue}>{tasks.filter(t => t.status === "in_progress").length}</div></div>
        <div style={S.statCard(C.danger)}><div style={S.statLabel}>Overdue</div><div style={S.statValue}>{tasks.filter(t => isOverdue(t)).length}</div></div>
        <div style={S.statCard(C.success)}><div style={S.statLabel}>Done</div><div style={S.statValue}>{tasks.filter(t => t.status === "done").length}</div></div>
      </div>

      {view === "kanban" && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          {TASK_STATUSES.map(s => <KanbanCol key={s} status={s} />)}
        </div>
      )}

      {view === "list" && (
        <div style={S.card}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Title</th>
                <th style={S.th}>Category</th>
                <th style={S.th}>Client</th>
                <th style={S.th}>Due</th>
                <th style={S.th}>Priority</th>
                <th style={S.th}>Status</th>
                <th style={S.th}>Checklist</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0
                ? <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: 32 }}>No tasks yet. Click "+ New Task" to get started.</td></tr>
                : filtered.map(t => (
                  <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => { setForm({ ...t }); setModal("edit"); }}>
                    <td style={S.td}>
                      <strong>{t.title}</strong>
                      {t.is_recurring && <span style={{ marginLeft: 6, fontSize: 10, background: C.primaryDim, color: C.primary, borderRadius: 10, padding: "1px 7px" }}>↻ {t.recurrence}</span>}
                    </td>
                    <td style={S.td}><span style={{ fontSize: 11, background: C.surface2, borderRadius: 10, padding: "2px 8px" }}>{(t.category || "other").replace(/_/g, " ")}</span></td>
                    <td style={S.td}>{t.client_name || "—"}</td>
                    <td style={{ ...S.td, color: isOverdue(t) ? C.danger : undefined }}>{t.due_date || "—"}{isOverdue(t) && " ⚠"}</td>
                    <td style={S.td}><span style={{ color: PRIORITY_COLOR[t.priority], fontWeight: 700, fontSize: 12 }}>● {t.priority}</span></td>
                    <td style={S.td}><Badge status={t.status} /></td>
                    <td style={S.td}>
                      {t.checklist?.length > 0
                        ? <span style={{ fontSize: 12 }}>{t.checklist.filter(c => c.done).length}/{t.checklist.length}</span>
                        : "—"}
                    </td>
                    <td style={S.td} onClick={e => { e.stopPropagation(); del(t.id); }}><button style={S.btn("ghost")}>🗑</button></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}

      {modal === "edit" && (
        <Modal title={form.id ? "Edit Task" : "New Accounting Task"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "span 2" }}>
              <label style={S.label}>Title *</label>
              <input style={S.input} value={form.title || ""} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Q2 VAT Return" />
            </div>
            <div>
              <label style={S.label}>Category</label>
              <select style={S.input} value={form.category || "other"} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {TASK_CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Priority</label>
              <select style={S.input} value={form.priority || "medium"} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}>
                {TASK_PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Status</label>
              <select style={S.input} value={form.status || "todo"} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {TASK_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Due Date</label>
              <input style={S.input} type="date" value={form.due_date || ""} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
            </div>
            <div>
              <label style={S.label}>Client</label>
              <select style={S.input} value={form.client_id || ""} onChange={e => {
                const c = customers.find(c => c.id === e.target.value);
                setForm(p => ({ ...p, client_id: c?.id || "", client_name: c?.name || "" }));
              }}>
                <option value="">None</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} — {c.company}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Assigned To</label>
              <input style={S.input} value={form.assigned_to || ""} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} placeholder="Name or email" />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={S.label}>Description</label>
              <textarea style={{ ...S.input, height: 64 }} value={form.description || ""} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: 16 }}>
              <label style={{ ...S.label, display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0 }}>
                <input type="checkbox" checked={!!form.is_recurring} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))} />
                Recurring task
              </label>
              {form.is_recurring && (
                <select style={{ ...S.input, width: "auto" }} value={form.recurrence || "monthly"} onChange={e => setForm(p => ({ ...p, recurrence: e.target.value }))}>
                  {["weekly","monthly","quarterly","yearly"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              )}
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={S.label}>Checklist</label>
              {(form.checklist || []).map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6, alignItems: "center" }}>
                  <input type="checkbox" checked={item.done} onChange={() => setForm(p => ({ ...p, checklist: p.checklist.map((c, j) => j === i ? { ...c, done: !c.done } : c) }))} />
                  <input style={{ ...S.input, flex: 1 }} value={item.label} onChange={e => updateCheckLabel(i, e.target.value)} placeholder="Checklist item..." />
                  <button style={S.btn("ghost")} onClick={() => removeCheckItem(i)}>✕</button>
                </div>
              ))}
              <button style={{ ...S.btn("secondary"), fontSize: 12, marginTop: 4 }} onClick={addCheckItem}>+ Add item</button>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={save}>Save Task</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── BANK & RECONCILIATION ───────────────────────────────────────────────────

function Bank({ bankAccounts, setBankAccounts, transactions, setTransactions }) {
  const [tab, setTab]       = useState("accounts");
  const [modal, setModal]   = useState(null);
  const [form, setForm]     = useState({});
  const [selAccount, setSelAccount] = useState(null);
  const [search, setSearch] = useState("");

  const saveAccount = () => {
    if (!form.id) setBankAccounts(p => [{ ...form, id: uid(), currency: "EUR", current_balance: form.opening_balance || 0, is_active: true, is_locked: false }, ...p]);
    else setBankAccounts(p => p.map(a => a.id === form.id ? form : a));
    setModal(null);
  };
  const delAccount = (id) => { if (window.confirm("Delete this account?")) setBankAccounts(p => p.filter(a => a.id !== id)); };

  const saveTx = () => {
    const tx = { ...form, id: form.id || uid(), source: form.source || "manual", is_reconciled: form.is_reconciled || false };
    if (!form.id) setTransactions(p => [tx, ...p]);
    else setTransactions(p => p.map(t => t.id === tx.id ? tx : t));
    setModal(null);
  };
  const delTx = (id) => { if (window.confirm("Delete transaction?")) setTransactions(p => p.filter(t => t.id !== id)); };
  const reconcile = (txId) => setTransactions(p => p.map(t => t.id === txId ? { ...t, is_reconciled: !t.is_reconciled } : t));

  const acctTxs = transactions.filter(t =>
    (!selAccount || t.account_name === selAccount?.account_name) &&
    `${t.description} ${t.reference || ""}`.toLowerCase().includes(search.toLowerCase())
  );
  const credits  = acctTxs.filter(t => t.type === "credit").reduce((s, t) => s + t.amount, 0);
  const debits   = acctTxs.filter(t => t.type === "debit").reduce((s, t) => s + t.amount, 0);
  const unrecon  = acctTxs.filter(t => !t.is_reconciled).length;

  const TabBtn = ({ id, label }) => (
    <button style={{ padding: "8px 18px", border: "none", cursor: "pointer", fontWeight: 600, fontSize: 13, borderBottom: tab === id ? `2px solid ${C.primary}` : "2px solid transparent", background: "transparent", color: tab === id ? C.primary : "#64748b" }} onClick={() => setTab(id)}>{label}</button>
  );

  return (
    <div>
      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, marginBottom: 20 }}>
        <TabBtn id="accounts" label="Accounts" />
        <TabBtn id="transactions" label="Transactions" />
      </div>

      {tab === "accounts" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button style={S.btn()} onClick={() => { setForm({ currency: "EUR", opening_balance: 0 }); setModal("account"); }}>+ Add Account</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
            {bankAccounts.length === 0 && <p style={{ color: "#94a3b8" }}>No bank accounts yet.</p>}
            {bankAccounts.map(acc => (
              <div key={acc.id} style={{ ...S.card, borderTop: `3px solid ${C.primary}`, cursor: "pointer" }} onClick={() => { setSelAccount(acc); setTab("transactions"); }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{acc.account_name}</div>
                    <div style={{ color: "#64748b", fontSize: 13 }}>{acc.bank_name}</div>
                    {acc.iban && <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>{acc.iban}</div>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 800, fontSize: 20 }}>{fmt(acc.current_balance)}</div>
                    <div style={{ color: "#94a3b8", fontSize: 11 }}>{acc.currency}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button style={{ ...S.btn("secondary"), fontSize: 12 }} onClick={e => { e.stopPropagation(); setForm({ ...acc }); setModal("account"); }}>Edit</button>
                  <button style={{ ...S.btn("ghost"), fontSize: 12 }} onClick={e => { e.stopPropagation(); delAccount(acc.id); }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "transactions" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 10 }}>
              <select style={{ ...S.input, width: "auto" }} value={selAccount?.id || ""} onChange={e => setSelAccount(bankAccounts.find(a => a.id === e.target.value) || null)}>
                <option value="">All accounts</option>
                {bankAccounts.map(a => <option key={a.id} value={a.id}>{a.account_name}</option>)}
              </select>
              <input style={S.searchBar} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button style={S.btn()} onClick={() => { setForm({ date: todayISO(), type: "debit", source: "manual", is_reconciled: false, account_name: selAccount?.account_name || "" }); setModal("tx"); }}>+ Add Transaction</button>
          </div>
          <div style={S.statGrid}>
            <div style={S.statCard(C.success)}><div style={S.statLabel}>Credits In</div><div style={S.statValue}>{fmt(credits)}</div></div>
            <div style={S.statCard(C.danger)}><div style={S.statLabel}>Debits Out</div><div style={S.statValue}>{fmt(debits)}</div></div>
            <div style={S.statCard(C.primary)}><div style={S.statLabel}>Net</div><div style={S.statValue}>{fmt(credits - debits)}</div></div>
            <div style={S.statCard(C.warning)}><div style={S.statLabel}>Unreconciled</div><div style={S.statValue}>{unrecon}</div></div>
          </div>
          <div style={S.card}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Date</th><th style={S.th}>Description</th><th style={S.th}>Account</th><th style={S.th}>Ref</th><th style={S.th}>Type</th><th style={S.th}>Amount</th><th style={S.th}>Category</th><th style={S.th}>Reconciled</th><th style={S.th}></th></tr></thead>
              <tbody>
                {acctTxs.length === 0
                  ? <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#94a3b8", padding: 32 }}>No transactions yet.</td></tr>
                  : acctTxs.map(tx => (
                    <tr key={tx.id} style={{ background: tx.is_reconciled ? C.successDim : "transparent" }}>
                      <td style={S.td}>{tx.date}</td>
                      <td style={S.td}><strong style={{ cursor: "pointer" }} onClick={() => { setForm({ ...tx }); setModal("tx"); }}>{tx.description}</strong></td>
                      <td style={S.td}>{tx.account_name}</td>
                      <td style={S.td}>{tx.reference || "—"}</td>
                      <td style={S.td}><span style={{ color: tx.type === "credit" ? C.success : C.danger, fontWeight: 700 }}>{tx.type}</span></td>
                      <td style={S.td}><strong style={{ color: tx.type === "credit" ? C.success : C.danger }}>{tx.type === "credit" ? "+" : "-"}{fmt(tx.amount)}</strong></td>
                      <td style={S.td}>{tx.category || "—"}</td>
                      <td style={S.td}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                          <input type="checkbox" checked={!!tx.is_reconciled} onChange={() => reconcile(tx.id)} />
                          {tx.is_reconciled ? <span style={{ color: C.success, fontSize: 12 }}>Done</span> : <span style={{ color: C.warning, fontSize: 12 }}>Pending</span>}
                        </label>
                      </td>
                      <td style={S.td}><button style={S.btn("ghost")} onClick={() => delTx(tx.id)}>X</button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal === "account" && (
        <Modal title={form.id ? "Edit Account" : "New Bank Account"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {[["Account Name *","account_name"],["Bank Name *","bank_name"],["IBAN","iban"],["BIC","bic"],["Account Number","account_number"]].map(([label, field]) => (
              <div key={field}><label style={S.label}>{label}</label><input style={S.input} value={form[field] || ""} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} /></div>
            ))}
            <div><label style={S.label}>Opening Balance</label><input style={S.input} type="number" value={form.opening_balance ?? 0} onChange={e => setForm(p => ({ ...p, opening_balance: +e.target.value }))} /></div>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Notes</label><textarea style={{ ...S.input, height: 56 }} value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={saveAccount}>Save</button>
          </div>
        </Modal>
      )}

      {modal === "tx" && (
        <Modal title={form.id ? "Edit Transaction" : "New Transaction"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div><label style={S.label}>Date *</label><input style={S.input} type="date" value={form.date || ""} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
            <div><label style={S.label}>Type</label>
              <select style={S.input} value={form.type || "debit"} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="credit">Credit (money in)</option>
                <option value="debit">Debit (money out)</option>
              </select>
            </div>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Description *</label><input style={S.input} value={form.description || ""} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div><label style={S.label}>Amount (EUR)</label><input style={S.input} type="number" step="0.01" value={form.amount || ""} onChange={e => setForm(p => ({ ...p, amount: +e.target.value }))} /></div>
            <div><label style={S.label}>Reference</label><input style={S.input} value={form.reference || ""} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} /></div>
            <div><label style={S.label}>Account</label>
              <select style={S.input} value={form.account_name || ""} onChange={e => setForm(p => ({ ...p, account_name: e.target.value }))}>
                <option value="">Select...</option>
                {bankAccounts.map(a => <option key={a.id} value={a.account_name}>{a.account_name}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Category</label><input style={S.input} value={form.category || ""} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Sales, Wages" /></div>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Notes</label><textarea style={{ ...S.input, height: 56 }} value={form.notes || ""} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={saveTx}>Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── CALENDAR ─────────────────────────────────────────────────────────────────

const EVENT_TYPES  = ["task","reminder","meeting","delivery","restock","other"];
const EVENT_COLORS = { task:C.primary, reminder:C.warning, meeting:"#8b5cf6", delivery:"#06b6d4", restock:C.success, other:"#94a3b8" };
const MONTH_NAMES  = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES    = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

function CalendarPage({ events, setEvents }) {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState({});
  const [selected, setSelected] = useState(null);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay    = new Date(year, month, 1).getDay();
  const prevMonth   = () => { if (month === 0) { setMonth(11); setYear(y => y-1); } else setMonth(m => m-1); };
  const nextMonth   = () => { if (month === 11) { setMonth(0); setYear(y => y+1); } else setMonth(m => m+1); };

  const dateStr = (d) => `${year}-${String(month+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
  const eventsOn = (d) => events.filter(e => e.date === dateStr(d));

  const save = () => {
    if (!form.id) setEvents(p => [{ ...form, id: uid() }, ...p]);
    else setEvents(p => p.map(e => e.id === form.id ? form : e));
    setModal(null);
  };
  const del = (id) => { if (window.confirm("Delete event?")) { setEvents(p => p.filter(e => e.id !== id)); setSelected(null); } };

  const selectedEvents = selected ? events.filter(e => e.date === selected) : [];

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 20 }}>
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <button style={S.btn("ghost")} onClick={prevMonth}>Prev</button>
          <h2 style={{ margin: 0, fontWeight: 800 }}>{MONTH_NAMES[month]} {year}</h2>
          <button style={S.btn("ghost")} onClick={nextMonth}>Next</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2, marginBottom: 8 }}>
          {DAY_NAMES.map(d => <div key={d} style={{ textAlign: "center", fontWeight: 700, fontSize: 11, color: "#64748b", padding: "4px 0" }}>{d}</div>)}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 2 }}>
          {Array.from({ length: firstDay }).map((_, i) => <div key={"e"+i} />)}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const d = i + 1;
            const ds = dateStr(d);
            const dayEvs = eventsOn(d);
            const isToday = ds === todayISO();
            const isSel   = ds === selected;
            return (
              <div key={d} onClick={() => setSelected(ds)}
                style={{ minHeight: 70, padding: 4, borderRadius: 6, border: isSel ? `2px solid ${C.primary}` : `1px solid ${C.surface2}`, background: isToday ? C.primaryDim : C.surface, cursor: "pointer" }}>
                <div style={{ fontWeight: isToday ? 800 : 400, fontSize: 13, color: isToday ? C.primary : C.text, marginBottom: 2 }}>{d}</div>
                {dayEvs.slice(0,3).map(ev => (
                  <div key={ev.id} style={{ fontSize: 10, background: ev.colour || EVENT_COLORS[ev.event_type] || "#94a3b8", color: C.white, borderRadius: 3, padding: "1px 4px", marginBottom: 2, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{ev.title}</div>
                ))}
                {dayEvs.length > 3 && <div style={{ fontSize: 10, color: "#94a3b8" }}>+{dayEvs.length-3} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div style={{ ...S.card, marginBottom: 12 }}>
          <button style={{ ...S.btn(), width: "100%" }} onClick={() => { setForm({ date: selected || todayISO(), event_type: "task", colour: C.primary, is_recurring: false }); setModal("edit"); }}>
            + New Event{selected ? " on " + selected : ""}
          </button>
        </div>
        {selected && (
          <div style={{ ...S.card, marginBottom: 12 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>{selected}</h3>
            {selectedEvents.length === 0
              ? <div style={{ color: "#94a3b8", fontSize: 13 }}>No events this day.</div>
              : selectedEvents.map(ev => (
                <div key={ev.id} style={{ borderLeft: `3px solid ${ev.colour || EVENT_COLORS[ev.event_type] || "#94a3b8"}`, paddingLeft: 10, marginBottom: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{ev.title}</div>
                  {ev.start_time && <div style={{ fontSize: 12, color: "#64748b" }}>{ev.start_time}{ev.end_time ? " - " + ev.end_time : ""}</div>}
                  {ev.description && <div style={{ fontSize: 12, color: "#64748b" }}>{ev.description}</div>}
                  <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                    <button style={{ ...S.btn("secondary"), fontSize: 11, padding: "3px 10px" }} onClick={() => { setForm({ ...ev }); setModal("edit"); }}>Edit</button>
                    <button style={{ ...S.btn("ghost"), fontSize: 11, padding: "3px 10px" }} onClick={() => del(ev.id)}>Delete</button>
                  </div>
                </div>
              ))
            }
          </div>
        )}
        <div style={S.card}>
          <h3 style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700 }}>Upcoming</h3>
          {events.filter(e => e.date >= todayISO()).sort((a,b) => a.date.localeCompare(b.date)).slice(0,6).map(ev => (
            <div key={ev.id} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: ev.colour || EVENT_COLORS[ev.event_type] || "#94a3b8", marginTop: 4, flexShrink: 0 }} />
              <div><div style={{ fontSize: 12, fontWeight: 600 }}>{ev.title}</div><div style={{ fontSize: 11, color: "#94a3b8" }}>{ev.date}</div></div>
            </div>
          ))}
          {events.filter(e => e.date >= todayISO()).length === 0 && <div style={{ color: "#94a3b8", fontSize: 12 }}>No upcoming events</div>}
        </div>
      </div>

      {modal === "edit" && (
        <Modal title={form.id ? "Edit Event" : "New Event"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Title *</label><input style={S.input} value={form.title || ""} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} /></div>
            <div><label style={S.label}>Date *</label><input style={S.input} type="date" value={form.date || ""} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
            <div><label style={S.label}>End Date</label><input style={S.input} type="date" value={form.end_date || ""} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></div>
            <div><label style={S.label}>Start Time</label><input style={S.input} type="time" value={form.start_time || ""} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))} /></div>
            <div><label style={S.label}>End Time</label><input style={S.input} type="time" value={form.end_time || ""} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))} /></div>
            <div><label style={S.label}>Type</label>
              <select style={S.input} value={form.event_type || "task"} onChange={e => setForm(p => ({ ...p, event_type: e.target.value, colour: EVENT_COLORS[e.target.value] || "#94a3b8" }))}>
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Colour</label><input style={{ ...S.input, height: 38, padding: 4 }} type="color" value={form.colour || C.primary} onChange={e => setForm(p => ({ ...p, colour: e.target.value }))} /></div>
            <div style={{ gridColumn: "span 2" }}><label style={S.label}>Description</label><textarea style={{ ...S.input, height: 60 }} value={form.description || ""} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} /></div>
            <div style={{ gridColumn: "span 2", display: "flex", alignItems: "center", gap: 16 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={!!form.is_recurring} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))} />
                Recurring
              </label>
              {form.is_recurring && (
                <select style={{ ...S.input, width: "auto" }} value={form.recurrence || "weekly"} onChange={e => setForm(p => ({ ...p, recurrence: e.target.value }))}>
                  {["daily","weekly","monthly","yearly"].map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={save}>Save Event</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── CHART OF ACCOUNTS + JOURNAL ENTRIES ─────────────────────────────────────

// ── CHART OF ACCOUNTS (P4-CC26d-0) ──────────────────────────────────────────
// The five types accounts_type_chk actually permits
// (20260815120000_cc26a_chart_of_accounts.sql:139).
//
// ⚠️ IT IS `income`, NEVER `revenue`. The old list offered `revenue`, which is
// UNSTORABLE — a row typed that way could never be saved — and the mistake sat
// in three places: this array, the colour map, and the filter dropdown.
//
// ⛔ THE 15-ACCOUNT `DEFAULT_COA` THAT USED TO LIVE HERE IS DELETED, NOT
// CORRECTED. It was a second chart of accounts, in localStorage, that had never
// read public.accounts — and it was wrong in ways that put money on the wrong
// side of the balance sheet: it had 1100 as Bank and 1200 as Accounts
// Receivable when the real chart has 1100 Trade Debtors and 1200 Bank Current
// Account, exactly swapped. Someone reconciling a bank balance off that screen
// was reading debtors. Correcting it would have left a second copy free to
// drift again; CC26a-CC26c kept exactly one copy of the chart and this restores
// that. The screen now reads the real thing through listAccounts().
//
// 📌 The localStorage "accounts" key is deliberately LEFT IN PLACE, UNREAD. If
// anyone ever added a custom account it exists only there; leaving it costs
// nothing and destroys nothing, clearing it is irreversible and buys nothing.
const COA_TYPES  = ["asset","liability","equity","income","expense"];
const COA_COLORS = { asset:C.primary, liability:C.danger, equity:"#8b5cf6", income:C.success, expense:C.warning };

function Accounts({ accounts, setAccounts }) {
  const [tab, setTab]     = useState("coa");
  // modal / form: REMOVED (P4-CC26d-0-fix). Both modals this component had —
  // the "coa" account editor (CC26d-0) and the "journal" entry editor (this
  // fix) — are gone, so the state that drove them is dead. React state nobody
  // sets and nobody reads is not harmless: it is the scaffolding a removed
  // feature gets rebuilt on by accident. CC26d-1 can reintroduce it
  // deliberately, wired to public.accounts and public.journals.
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  // ── P4-CC26d-0: THE REAL CHART, READ-ONLY ────────────────────────────────
  // This tab used to render DEFAULT_COA out of localStorage — fifteen accounts
  // that had never touched public.accounts and were wrong in ways that mattered
  // (1100 and 1200 swapped between Bank and Debtors, 2100 labelled VAT when the
  // real 2100 is PAYE/PRSI/USC, and 4000/4100 typed `revenue`, which the
  // database will not even store). It now reads the database.
  //
  // FOUR STATES, EACH SAYING SOMETHING DIFFERENT, and the third is the point:
  // sales/driver/employee have no read on accounts, and RLS denies by returning
  // ZERO ROWS WITH NO ERROR. Showing that as an empty chart would tell a user
  // their books are empty when the truth is they are not allowed to look.
  const [coa, setCoa] = useState({ status: "loading", accounts: [], error: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await listAccounts();
        if (!alive) return;
        if (res.skipped) setCoa({ status: res.reason === "no-permission" ? "denied" : "unavailable", accounts: [], error: res.reason });
        else setCoa({ status: "ready", accounts: res.accounts, error: null });
      } catch (e) {
        // A real failure is shown as a failure, never as an empty chart.
        if (alive) setCoa({ status: "error", accounts: [], error: e.message || String(e) });
      }
    })();
    return () => { alive = false; };
  }, []);

  const filteredCoa = coa.accounts.filter(a =>
    (filterType === "all" || a.type === filterType) &&
    `${a.code} ${a.name}`.toLowerCase().includes(search.toLowerCase())
  );

  // blankLine / totalDebit / totalCredit / balanced / saveJournal: REMOVED
  // (P4-CC26d-0-fix), with the Journal Entries panel and modal they served. They
  // were the last things in this file that could write to localStorage.journals,
  // and leaving them defined would have left a working save path one JSX line
  // away from being reachable again.

  const TabBtn = ({ id, label }) => (
    <button style={{ padding:"8px 18px", border:"none", cursor:"pointer", fontWeight:600, fontSize:13, borderBottom: tab===id ? `2px solid ${C.primary}` : "2px solid transparent", background:"transparent", color: tab===id ? C.primary : "#64748b" }} onClick={() => setTab(id)}>{label}</button>
  );

  const grouped = COA_TYPES.reduce((acc, type) => { acc[type] = filteredCoa.filter(a => a.type === type); return acc; }, {});

  return (
    <div>
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:20 }}>
        <TabBtn id="coa" label="Chart of Accounts" />
        <TabBtn id="journals" label="Journal Entries" />
      </div>

      {tab === "coa" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16, gap:10 }}>
            <div style={{ display:"flex", gap:10 }}>
              <input style={S.searchBar} placeholder="Search accounts..." value={search} onChange={e => setSearch(e.target.value)} />
              <select style={{ ...S.input, width:"auto" }} value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="all">All types</option>
                {COA_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {/* ⛔ NO CREATE / EDIT / DELETE IN THIS PACKAGE (P4-CC26d-0).
                The "+ New Account" button and the per-row Edit buttons wrote to
                localStorage, so they edited a chart nothing else read. Writing
                real accounts needs an RBAC-aware data layer and its own harness
                — CC26d-1. A read-only screen that tells the truth beats an
                editable one that does not. */}
            <div style={{ fontSize:12, color:"#64748b", alignSelf:"center" }}>Read-only — editing arrives with CC26d-1</div>
          </div>

          {coa.status === "loading" && (
            <div style={{ ...S.card, padding:20, color:"#94a3b8" }}>Loading the chart of accounts…</div>
          )}

          {/* ⚠️ DENIED IS NOT EMPTY. RLS hides rows rather than raising, so
              without this the screen would say "no accounts" to a user whose
              company has a full chart they simply may not read. */}
          {coa.status === "denied" && (
            <div style={{ ...S.card, padding:20 }}>
              <div style={{ fontWeight:700, marginBottom:6 }}>You do not have access to the chart of accounts</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>
                Your role cannot view accounts. This does not mean the chart is empty — ask an owner or admin if you need access.
              </div>
            </div>
          )}

          {coa.status === "unavailable" && (
            <div style={{ ...S.card, padding:20 }}>
              <div style={{ fontWeight:700, marginBottom:6 }}>No company selected</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>Sign in again, or contact support if this persists.</div>
            </div>
          )}

          {coa.status === "error" && (
            <div style={{ ...S.card, padding:20 }}>
              <div style={{ fontWeight:700, marginBottom:6, color:C.danger }}>Could not load the chart of accounts</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>{coa.error}</div>
            </div>
          )}

          {coa.status === "ready" && coa.accounts.length === 0 && (
            <div style={{ ...S.card, padding:20 }}>
              <div style={{ fontWeight:700, marginBottom:6 }}>This company has no chart of accounts yet</div>
              <div style={{ fontSize:13, color:"#94a3b8" }}>
                It is created automatically on first sign-in. If you are seeing this, sign out and back in — or contact support.
              </div>
            </div>
          )}

          {coa.status === "ready" && coa.accounts.length > 0 && filteredCoa.length === 0 && (
            <div style={{ ...S.card, padding:20, color:"#94a3b8" }}>
              No accounts match this search or filter — {coa.accounts.length} in the chart.
            </div>
          )}

          {coa.status === "ready" && COA_TYPES.map(type => grouped[type]?.length > 0 && (
            <div key={type} style={{ marginBottom:20 }}>
              <div style={{ fontWeight:700, fontSize:12, textTransform:"uppercase", letterSpacing:"0.08em", color:COA_COLORS[type], marginBottom:8 }}>
                {type} ({grouped[type].length})
              </div>
              <div style={S.card}>
                <table style={S.table}>
                  {/* NO BALANCE COLUMN. public.accounts holds no balance — a
                      balance is a sum over journal_lines, and inventing one
                      here would be a second number nobody could reconcile.
                      Balances arrive with the trial balance (CC26d). The column
                      is `subtype`, not `sub_type`. */}
                  <thead><tr><th style={S.th}>Code</th><th style={S.th}>Name</th><th style={S.th}>Sub-type</th><th style={S.th}>Status</th></tr></thead>
                  <tbody>{grouped[type].map(a => (
                    <tr key={a.id} style={a.is_active === false ? { opacity:0.55 } : undefined}>
                      <td style={S.td}><strong>{a.code}</strong></td>
                      <td style={S.td}>{a.name}</td>
                      <td style={S.td}>{a.subtype || "—"}</td>
                      <td style={S.td}>
                        <span style={{ fontSize:11, color:"#94a3b8" }}>
                          {[a.is_system ? "System" : null, a.is_active === false ? "Inactive" : null].filter(Boolean).join(" · ") || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── JOURNAL ENTRIES — DISABLED, DELIBERATELY (P4-CC26d-0-fix) ────────
          The tab stays VISIBLE so the navigation does not change shape, but its
          panel no longer pretends to work.

          WHY THIS PACKAGE OWNS IT. Deleting DEFAULT_COA left this tab's account
          dropdown empty for any new user — the dropdown was populated from the
          same localStorage array. That empty dropdown is a CONSEQUENCE OF THIS
          PACKAGE, so it is this package's to fix; the brief saying "do not touch
          the journals tab" does not make a side effect somebody else's problem.

          AND THE HONEST VERSION IS THE SAFER ONE IN FRONT OF A CUSTOMER. A tab
          that LOOKS functional, offers an empty account dropdown and saves to
          localStorage is worse than one that plainly says it is not ready: the
          first invites someone to type a journal that goes nowhere and that the
          ledger never sees, the second sets an accurate expectation. The general
          ledger behind this screen is real — journals, double-entry balance
          enforcement, and posting from invoices and receipts all live in
          public.journals — it is only this UI that has not caught up.

          ⛔ EVERY WRITE PATH IS GONE: the "+ New Journal Entry" button, the
          row-click that opened the editor, the per-row delete, and the
          modal === "journal" block with saveJournal(). Nothing here can write to
          localStorage any more. Building the real tab against public.journals is
          CC26d-1. */}
      {tab === "journals" && (
        <div style={{ ...S.card, padding:28, textAlign:"center" }}>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:8 }}>Journal entries are coming with the trial balance</div>
          <div style={{ fontSize:13, color:"#94a3b8", maxWidth:560, margin:"0 auto", lineHeight:1.6 }}>
            The general ledger behind this screen is already live — double-entry journals, balance
            enforcement, and automatic posting from invoices and receipts. What is not ready yet is
            this screen for reviewing and entering journals by hand. It arrives with the trial
            balance, profit &amp; loss and balance sheet.
          </div>
        </div>
      )}

      {/* ⛔ THE "coa" MODAL IS REMOVED (P4-CC26d-0). It wrote a new or edited
          account into the localStorage array — a chart nothing else read —
          and its Type dropdown offered `revenue`, which accounts_type_chk
          would have rejected outright had it ever reached the database.
          Creating and editing real accounts is CC26d-1, with RBAC-aware
          plumbing and its own harness. Nothing sets setModal("coa") now. */}

      {/* ⛔ THE "journal" MODAL IS REMOVED (P4-CC26d-0-fix). It was the editor
          behind the disabled tab above: it built entries, checked they balanced
          in JavaScript, and saved them to localStorage where nothing else could
          ever read them. Balance is enforced by post_journal() in the database
          (20260816120000_cc26b_journals.sql) and a second, weaker copy of that
          rule in the UI is exactly the drift this project keeps removing.
          Nothing sets setModal("journal") now. */}
    </div>
  );
}

// ─── PAYROLL ─────────────────────────────────────────────────────────────────
// Irish PAYE/PRSI/USC auto-calculation (2025 rates, single person, standard)

function calcIrishPayroll(gross) {
  const g = +gross || 0;
  const monthly20 = 3500;
  const paye = g <= monthly20 ? g * 0.20 : monthly20 * 0.20 + (g - monthly20) * 0.40;
  const prsi_emp = g * 0.04;
  const prsi_er = g * 0.1115;
  let usc = 0;
  let rem = g;
  const bands = [[660, 0.005],[733, 0.02],[1633, 0.045],[Infinity, 0.08]];
  for (const [band, rate] of bands) {
    const taxable = Math.min(rem, band);
    usc += taxable * rate;
    rem -= taxable;
    if (rem <= 0) break;
  }
  const total_deductions = paye + prsi_emp + usc;
  const net = g - total_deductions;
  return {
    paye_tax:       +paye.toFixed(2),
    prsi_employee:  +prsi_emp.toFixed(2),
    prsi_employer:  +prsi_er.toFixed(2),
    usc:            +usc.toFixed(2),
    net_pay:        +net.toFixed(2),
  };
}

function Payroll({ payroll, setPayroll, staff }) {
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState({});
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = payroll
    .filter(p => filterStatus === "all" || p.status === filterStatus)
    .filter(p => `${p.employee_name} ${p.pps_number || ""}`.toLowerCase().includes(search.toLowerCase()));

  const totalGross = filtered.reduce((s, p) => s + (p.gross_pay || 0), 0);
  const totalNet   = filtered.reduce((s, p) => s + (p.net_pay || 0), 0);
  const totalPAYE  = filtered.reduce((s, p) => s + (p.paye_tax || 0), 0);
  const totalPRSI  = filtered.reduce((s, p) => s + (p.prsi_employer || 0), 0);

  const openNew = () => {
    const now = new Date();
    const start = localISO(new Date(now.getFullYear(), now.getMonth(), 1));
    const end   = localISO(new Date(now.getFullYear(), now.getMonth()+1, 0));
    setForm({ status:"draft", pay_period_start:start, pay_period_end:end, pay_date:todayISO(), gross_pay:0, pension_employee:0, pension_employer:0, other_deductions:0, ...calcIrishPayroll(0) });
    setModal("edit");
  };

  const fromStaff = (s) => {
    const gross = s.hourly_rate * 173.33;
    setForm(p => ({ ...p, employee_id:s.id, employee_name:s.name, employee_email:s.email || "", hourly_rate:s.hourly_rate, gross_pay:+gross.toFixed(2), ...calcIrishPayroll(gross) }));
  };

  const recalc = (gross, extra = {}) => {
    const base = calcIrishPayroll(gross);
    const pen_emp  = +(extra.pension_employee || form.pension_employee || 0);
    const pen_er   = +(extra.pension_employer || form.pension_employer || 0);
    const other    = +(extra.other_deductions || form.other_deductions || 0);
    const net = base.net_pay - pen_emp - other;
    setForm(p => ({ ...p, gross_pay:gross, ...base, pension_employee:pen_emp, pension_employer:pen_er, other_deductions:other, net_pay:+net.toFixed(2), ...extra }));
  };

  const save = () => {
    if (!form.id) setPayroll(p => [{ ...form, id:uid() }, ...p]);
    else setPayroll(p => p.map(x => x.id === form.id ? form : x));
    setModal(null);
  };
  const del = (id) => { if (window.confirm("Delete payroll record?")) setPayroll(p => p.filter(x => x.id !== id)); };
  const approve = (id) => setPayroll(p => p.map(x => x.id === id ? { ...x, status:"approved" } : x));
  const markPaid = (id) => setPayroll(p => p.map(x => x.id === id ? { ...x, status:"paid" } : x));

  const Row = ({ label, value, highlight }) => (
    <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.surface2}`, color: highlight ? C.danger : undefined }}>
      <span style={{ color:"#64748b", fontSize:13 }}>{label}</span>
      <strong style={{ fontSize:13 }}>{fmt(value)}</strong>
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16, gap:10, flexWrap:"wrap" }}>
        <div style={{ display:"flex", gap:10 }}>
          <input style={S.searchBar} placeholder="Search payroll..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...S.input, width:"auto" }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All</option>
            {["draft","approved","paid"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <button style={S.btn()} onClick={openNew}>+ New Payroll Record</button>
      </div>

      <div style={S.statGrid}>
        <div style={S.statCard(C.textMuted)}><div style={S.statLabel}>Total Gross</div><div style={S.statValue}>{fmt(totalGross)}</div></div>
        <div style={S.statCard(C.success)}><div style={S.statLabel}>Total Net Pay</div><div style={S.statValue}>{fmt(totalNet)}</div></div>
        <div style={S.statCard(C.danger)}><div style={S.statLabel}>Total PAYE</div><div style={S.statValue}>{fmt(totalPAYE)}</div></div>
        <div style={S.statCard(C.warning)}><div style={S.statLabel}>Employer PRSI</div><div style={S.statValue}>{fmt(totalPRSI)}</div></div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Employee</th><th style={S.th}>PPS</th><th style={S.th}>Period</th>
            <th style={S.th}>Gross</th><th style={S.th}>PAYE</th><th style={S.th}>PRSI</th><th style={S.th}>USC</th><th style={S.th}>Net Pay</th>
            <th style={S.th}>Status</th><th style={S.th}></th>
          </tr></thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={10} style={{ ...S.td, textAlign:"center", color:"#94a3b8", padding:32 }}>No payroll records. Click "+ New Payroll Record" to start.</td></tr>
              : filtered.map(p => (
                <tr key={p.id} style={{ cursor:"pointer" }} onClick={() => { setForm({...p}); setModal("edit"); }}>
                  <td style={S.td}><strong>{p.employee_name}</strong><div style={{ fontSize:11, color:"#94a3b8" }}>{p.employee_email}</div></td>
                  <td style={S.td}>{p.pps_number || "—"}</td>
                  <td style={S.td}><div style={{ fontSize:12 }}>{p.pay_period_start}</div><div style={{ fontSize:11, color:"#94a3b8" }}>to {p.pay_period_end}</div></td>
                  <td style={S.td}><strong>{fmt(p.gross_pay)}</strong></td>
                  <td style={S.td}>{fmt(p.paye_tax)}</td>
                  <td style={S.td}>{fmt(p.prsi_employee)}</td>
                  <td style={S.td}>{fmt(p.usc)}</td>
                  <td style={S.td}><strong style={{ color:C.success }}>{fmt(p.net_pay)}</strong></td>
                  <td style={S.td}><Badge status={p.status} /></td>
                  <td style={S.td} onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", gap:4 }}>
                      {p.status === "draft"    && <button style={{ ...S.btn("secondary"), fontSize:11, padding:"3px 8px" }} onClick={() => approve(p.id)}>Approve</button>}
                      {p.status === "approved" && <button style={{ ...S.btn(), fontSize:11, padding:"3px 8px" }} onClick={() => markPaid(p.id)}>Mark Paid</button>}
                      <button style={S.btn("ghost")} onClick={() => del(p.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {modal === "edit" && (
        <Modal title={form.id ? "Edit Payroll Record" : "New Payroll Record"} onClose={() => setModal(null)}>
          {staff.length > 0 && !form.id && (
            <div style={{ marginBottom:16 }}>
              <label style={S.label}>Quick-fill from Staff Member</label>
              <select style={S.input} defaultValue="" onChange={e => { const s = staff.find(x => x.id === e.target.value); if(s) fromStaff(s); }}>
                <option value="">Select staff member...</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name} — {s.role}</option>)}
              </select>
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div><label style={S.label}>Employee Name *</label><input style={S.input} value={form.employee_name||""} onChange={e => setForm(p=>({...p,employee_name:e.target.value}))} /></div>
            <div><label style={S.label}>PPS Number</label><input style={S.input} value={form.pps_number||""} onChange={e => setForm(p=>({...p,pps_number:e.target.value}))} placeholder="1234567A" /></div>
            <div><label style={S.label}>Email</label><input style={S.input} type="email" value={form.employee_email||""} onChange={e => setForm(p=>({...p,employee_email:e.target.value}))} /></div>
            <div><label style={S.label}>Pay Date</label><input style={S.input} type="date" value={form.pay_date||""} onChange={e => setForm(p=>({...p,pay_date:e.target.value}))} /></div>
            <div><label style={S.label}>Period Start</label><input style={S.input} type="date" value={form.pay_period_start||""} onChange={e => setForm(p=>({...p,pay_period_start:e.target.value}))} /></div>
            <div><label style={S.label}>Period End</label><input style={S.input} type="date" value={form.pay_period_end||""} onChange={e => setForm(p=>({...p,pay_period_end:e.target.value}))} /></div>
            <div><label style={S.label}>Gross Pay (€) *</label><input style={S.input} type="number" step="0.01" value={form.gross_pay||""} onChange={e => recalc(+e.target.value)} /></div>
            <div><label style={S.label}>Hours Worked</label><input style={S.input} type="number" value={form.hours_worked||""} onChange={e => setForm(p=>({...p,hours_worked:+e.target.value}))} /></div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
            <div style={{ gridColumn:"span 2", background:C.surface, borderRadius:8, padding:14 }}>
              <div style={{ fontWeight:700, fontSize:12, marginBottom:10, color:"#475569", textTransform:"uppercase", letterSpacing:"0.06em" }}>Irish Payroll Calculation (auto)</div>
              <Row label="Gross Pay" value={form.gross_pay} />
              <Row label="PAYE Tax" value={form.paye_tax} highlight />
              <Row label="PRSI (Employee 4%)" value={form.prsi_employee} highlight />
              <Row label="USC" value={form.usc} highlight />
              <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.surface2}` }}>
                <span style={{ color:"#64748b", fontSize:13 }}>Pension (Employee)</span>
                <input style={{ ...S.input, width:100, textAlign:"right" }} type="number" value={form.pension_employee||0} onChange={e => recalc(form.gross_pay, { pension_employee:+e.target.value })} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${C.surface2}` }}>
                <span style={{ color:"#64748b", fontSize:13 }}>Other Deductions</span>
                <input style={{ ...S.input, width:100, textAlign:"right" }} type="number" value={form.other_deductions||0} onChange={e => recalc(form.gross_pay, { other_deductions:+e.target.value })} />
              </div>
              <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", fontWeight:800, fontSize:15, borderTop:`2px solid ${C.border}`, marginTop:4 }}>
                <span>Net Pay</span><span style={{ color:C.success }}>{fmt(form.net_pay)}</span>
              </div>
              <div style={{ marginTop:8, padding:"6px 0", borderTop:`1px solid ${C.surface2}`, display:"flex", justifyContent:"space-between" }}>
                <span style={{ color:"#64748b", fontSize:12 }}>Employer PRSI (11.15%)</span>
                <span style={{ fontWeight:700, fontSize:12, color:C.warning }}>{fmt(form.prsi_employer)}</span>
              </div>
            </div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginTop:14 }}>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status||"draft"} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                {["draft","approved","paid"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Notes</label><input style={S.input} value={form.notes||""} onChange={e => setForm(p=>({...p,notes:e.target.value}))} /></div>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={save}>Save Record</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── STAFF & ROTA ────────────────────────────────────────────────────────────

const DAYS = ["mon","tue","wed","thu","fri","sat","sun"];
const DAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const CONTRACT_TYPES = ["full_time","part_time","casual","zero_hours"];

function StaffRota({ staff, setStaff, shifts, setShifts }) {
  const [tab, setTab]     = useState("staff");
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState({});
  const [search, setSearch] = useState("");
  const [rotaWeek, setRotaWeek] = useState(() => {
    // Live Class 2 bug until fixed here (CC20): this used to construct `d` at the
    // CURRENT instant (real time-of-day, not midnight) and round-trip it through
    // toISOString(), which is wrong for the ~1hr/day window between local midnight and
    // 01:00 during BST — and unlike display-only sites, this value gets written into
    // shifts[].date the moment anyone adds a shift, so a wrong rotaWeek here doesn't
    // just look wrong, it PERSISTS wrong.
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    return localISO(d);
  });

  // parseLocalDate(rotaWeek), not `new Date(rotaWeek)`. ROBUSTNESS ONLY, not a live bug:
  // CC20 Phase 0 proved (empirically, under TZ=Europe/Dublin, including across both 2026
  // DST transitions) that `new Date(rotaWeek)` -> getDate()/setDate() -> toISOString()
  // round-trips correctly here specifically because Ireland's UTC offset is never
  // negative — UTC midnight of any date still falls on the SAME local calendar day. It
  // breaks at the first negative offset (a US-based user, a CI runner in a different
  // TZ). Do not revert this to `new Date(rotaWeek)` as an "unnecessary change" — it is
  // unnecessary only for as long as this app is only ever used from a non-negative UTC
  // offset, which is not a guarantee this file can see.
  const weekDates = Array.from({length:7}, (_, i) => {
    const d = parseLocalDate(rotaWeek);
    d.setDate(d.getDate() + i);
    return localISO(d);
  });

  const prevWeek = () => { const d = parseLocalDate(rotaWeek); d.setDate(d.getDate()-7); setRotaWeek(localISO(d)); };
  const nextWeek = () => { const d = parseLocalDate(rotaWeek); d.setDate(d.getDate()+7); setRotaWeek(localISO(d)); };

  const shiftsOn = (staffId, date) => shifts.filter(s => s.staff_id === staffId && s.date === date);
  const calcHours = (start, end, brk=30) => {
    if (!start || !end) return 0;
    const [sh,sm] = start.split(":").map(Number);
    const [eh,em] = end.split(":").map(Number);
    return Math.max(0, ((eh*60+em) - (sh*60+sm) - brk) / 60);
  };

  const weeklyHours = (staffId) => weekDates.reduce((sum, date) => {
    return sum + shiftsOn(staffId, date).reduce((s,sh) => s + calcHours(sh.start_time, sh.end_time, sh.break_minutes), 0);
  }, 0);

  const weeklyWage = (staffId) => {
    const s = staff.find(x => x.id === staffId);
    return (s?.hourly_rate || 0) * weeklyHours(staffId);
  };

  const addShift = (staffId, staffName, date) => {
    setForm({ staff_id:staffId, staff_name:staffName, date, start_time:"09:00", end_time:"17:00", break_minutes:30, status:"scheduled", colour: staff.find(s=>s.id===staffId)?.colour || "#06b6d4" });
    setModal("shift");
  };

  const saveShift = () => {
    const hours = calcHours(form.start_time, form.end_time, form.break_minutes);
    const sh = { ...form, id:form.id||uid(), hours:+hours.toFixed(2) };
    if (!form.id) setShifts(p => [...p, sh]);
    else setShifts(p => p.map(x => x.id === sh.id ? sh : x));
    setModal(null);
  };

  const saveStaff = () => {
    if (!form.id) setStaff(p => [{ ...form, id:uid(), hourly_rate:form.hourly_rate||13.50, colour:form.colour||"#06b6d4", status:"active", availability:{mon:true,tue:true,wed:true,thu:true,fri:true,sat:false,sun:false} }, ...p]);
    else setStaff(p => p.map(s => s.id === form.id ? form : s));
    setModal(null);
  };

  const delStaff = (id) => { if (window.confirm("Delete staff member?")) setStaff(p => p.filter(s => s.id !== id)); };
  const delShift = (id) => { if (window.confirm("Delete shift?")) setShifts(p => p.filter(s => s.id !== id)); };

  const totalWeekHours = staff.reduce((s, m) => s + weeklyHours(m.id), 0);
  const totalWeekWage  = staff.reduce((s, m) => s + weeklyWage(m.id), 0);

  const TabBtn = ({ id, label }) => (
    <button style={{ padding:"8px 18px", border:"none", cursor:"pointer", fontWeight:600, fontSize:13, borderBottom: tab===id ? `2px solid ${C.primary}` : "2px solid transparent", background:"transparent", color: tab===id ? C.primary : "#64748b" }} onClick={() => setTab(id)}>{label}</button>
  );

  return (
    <div>
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:20 }}>
        <TabBtn id="staff" label="Staff Members" />
        <TabBtn id="rota"  label="Weekly Rota" />
      </div>

      {tab === "staff" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16 }}>
            <input style={S.searchBar} placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)} />
            <button style={S.btn()} onClick={() => { setForm({ contract_type:"part_time", hourly_rate:13.50, colour:"#06b6d4", status:"active", availability:{mon:true,tue:true,wed:true,thu:true,fri:true,sat:false,sun:false} }); setModal("staff"); }}>+ Add Staff</button>
          </div>
          <div style={S.card}>
            <table style={S.table}>
              <thead><tr><th style={S.th}>Name</th><th style={S.th}>Role</th><th style={S.th}>Contract</th><th style={S.th}>Rate</th><th style={S.th}>Availability</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
              <tbody>
                {staff.filter(s => `${s.name} ${s.role||""}`.toLowerCase().includes(search.toLowerCase())).length === 0
                  ? <tr><td colSpan={7} style={{ ...S.td, textAlign:"center", color:"#94a3b8", padding:32 }}>No staff members yet.</td></tr>
                  : staff.filter(s => `${s.name} ${s.role||""}`.toLowerCase().includes(search.toLowerCase())).map(s => (
                    <tr key={s.id} style={{ cursor:"pointer" }} onClick={() => { setForm({...s}); setModal("staff"); }}>
                      <td style={S.td}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <div style={{ width:10, height:10, borderRadius:"50%", background:s.colour||"#06b6d4", flexShrink:0 }} />
                          <strong>{s.name}</strong>
                        </div>
                        {s.email && <div style={{ fontSize:11, color:"#94a3b8", marginLeft:18 }}>{s.email}</div>}
                      </td>
                      <td style={S.td}>{s.role||"—"}</td>
                      <td style={S.td}>{(s.contract_type||"").replace(/_/g," ")}</td>
                      <td style={S.td}><strong>€{(s.hourly_rate||0).toFixed(2)}/hr</strong></td>
                      <td style={S.td}>
                        <div style={{ display:"flex", gap:3 }}>
                          {DAYS.map((d,i) => (
                            <span key={d} style={{ fontSize:10, fontWeight:700, padding:"2px 4px", borderRadius:3, background: s.availability?.[d] ? C.primaryDim : C.surface2, color: s.availability?.[d] ? "#0e9cba" : "#94a3b8" }}>{DAY_LABELS[i]}</span>
                          ))}
                        </div>
                      </td>
                      <td style={S.td}><Badge status={s.status||"active"} /></td>
                      <td style={S.td} onClick={e => { e.stopPropagation(); delStaff(s.id); }}><button style={S.btn("ghost")}>🗑</button></td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "rota" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ display:"flex", gap:10, alignItems:"center" }}>
              <button style={S.btn("ghost")} onClick={prevWeek}>← Prev</button>
              <strong>Week of {rotaWeek}</strong>
              <button style={S.btn("ghost")} onClick={nextWeek}>Next →</button>
            </div>
            <div style={{ fontSize:13, color:"#64748b" }}>Total: <strong>{totalWeekHours.toFixed(1)}h</strong> · Cost: <strong>{fmt(totalWeekWage)}</strong></div>
          </div>

          {staff.length === 0
            ? <div style={{ ...S.card, textAlign:"center", padding:32, color:"#94a3b8" }}>Add staff members first, then build the rota.</div>
            : (
              <div style={{ overflowX:"auto" }}>
                <table style={{ ...S.table, minWidth:900 }}>
                  <thead>
                    <tr>
                      <th style={{ ...S.th, width:160 }}>Staff</th>
                      {weekDates.map((date,i) => (
                        <th key={date} style={{ ...S.th, textAlign:"center", background: date === todayISO() ? C.primaryDim : undefined }}>
                          <div>{DAY_LABELS[i]}</div>
                          <div style={{ fontWeight:400, fontSize:11, color:"#94a3b8" }}>{date.slice(5)}</div>
                        </th>
                      ))}
                      <th style={S.th}>Week Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(s => (
                      <tr key={s.id}>
                        <td style={{ ...S.td, fontWeight:600, fontSize:13 }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ width:8, height:8, borderRadius:"50%", background:s.colour||"#06b6d4" }} />
                            {s.name}
                          </div>
                          <div style={{ fontSize:11, color:"#94a3b8" }}>€{(s.hourly_rate||0).toFixed(2)}/hr</div>
                        </td>
                        {weekDates.map(date => {
                          const dayShifts = shiftsOn(s.id, date);
                          const avail = s.availability?.[DAYS[weekDates.indexOf(date)]];
                          return (
                            <td key={date} style={{ ...S.td, textAlign:"center", verticalAlign:"top", background: date === todayISO() ? C.surface : !avail ? C.surface : undefined, padding:"6px 4px", minWidth:110 }}>
                              {dayShifts.map(sh => (
                                <div key={sh.id} style={{ background:sh.colour||s.colour||"#06b6d4", color:C.white, borderRadius:4, padding:"3px 6px", fontSize:11, marginBottom:3, cursor:"pointer", lineHeight:1.4 }}
                                  onClick={() => { setForm({...sh}); setModal("shift"); }}>
                                  {sh.start_time}–{sh.end_time}
                                  <div style={{ fontSize:10, opacity:0.85 }}>{calcHours(sh.start_time,sh.end_time,sh.break_minutes).toFixed(1)}h</div>
                                </div>
                              ))}
                              <button style={{ fontSize:18, background:"none", border:`1px dashed ${C.border}`, color:"#94a3b8", borderRadius:4, padding:"1px 8px", cursor:"pointer", marginTop:dayShifts.length?3:0, width:"100%" }}
                                onClick={() => addShift(s.id, s.name, date)}>+</button>
                            </td>
                          );
                        })}
                        <td style={{ ...S.td, textAlign:"right", fontWeight:700 }}>
                          <div>{weeklyHours(s.id).toFixed(1)}h</div>
                          <div style={{ fontSize:12, color:"#64748b" }}>{fmt(weeklyWage(s.id))}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </div>
      )}

      {modal === "staff" && (
        <Modal title={form.id ? "Edit Staff Member" : "New Staff Member"} onClose={() => setModal(null)}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Name *</label><input style={S.input} value={form.name||""} onChange={e => setForm(p=>({...p,name:e.target.value}))} /></div>
            <div><label style={S.label}>Role / Job Title</label><input style={S.input} value={form.role||""} onChange={e => setForm(p=>({...p,role:e.target.value}))} placeholder="e.g. Barista, Driver" /></div>
            <div><label style={S.label}>Email</label><input style={S.input} type="email" value={form.email||""} onChange={e => setForm(p=>({...p,email:e.target.value}))} /></div>
            <div><label style={S.label}>Phone</label><input style={S.input} value={form.phone||""} onChange={e => setForm(p=>({...p,phone:e.target.value}))} /></div>
            <div><label style={S.label}>Hourly Rate (€)</label><input style={S.input} type="number" step="0.01" value={form.hourly_rate||13.50} onChange={e => setForm(p=>({...p,hourly_rate:+e.target.value}))} /></div>
            <div><label style={S.label}>Contract Type</label>
              <select style={S.input} value={form.contract_type||"part_time"} onChange={e => setForm(p=>({...p,contract_type:e.target.value}))}>
                {CONTRACT_TYPES.map(c => <option key={c} value={c}>{c.replace(/_/g," ")}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status||"active"} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                {["active","inactive","on_leave"].map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Colour on Rota</label><input style={{ ...S.input, height:38, padding:4 }} type="color" value={form.colour||"#06b6d4"} onChange={e => setForm(p=>({...p,colour:e.target.value}))} /></div>
            <div><label style={S.label}>Start Date</label><input style={S.input} type="date" value={form.start_date||""} onChange={e => setForm(p=>({...p,start_date:e.target.value}))} /></div>
            <div style={{ gridColumn:"span 2" }}>
              <label style={S.label}>Availability</label>
              <div style={{ display:"flex", gap:8 }}>
                {DAYS.map((d,i) => (
                  <label key={d} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, cursor:"pointer", fontSize:12, fontWeight:600 }}>
                    {DAY_LABELS[i]}
                    <input type="checkbox" checked={!!form.availability?.[d]} onChange={e => setForm(p => ({ ...p, availability:{ ...(p.availability||{}), [d]:e.target.checked } }))} />
                  </label>
                ))}
              </div>
            </div>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Notes</label><textarea style={{ ...S.input, height:56 }} value={form.notes||""} onChange={e => setForm(p=>({...p,notes:e.target.value}))} /></div>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={saveStaff}>Save</button>
          </div>
        </Modal>
      )}

      {modal === "shift" && (
        <Modal title={form.id ? "Edit Shift" : `New Shift — ${form.staff_name}`} onClose={() => setModal(null)}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div><label style={S.label}>Date</label><input style={S.input} type="date" value={form.date||""} onChange={e => setForm(p=>({...p,date:e.target.value}))} /></div>
            <div><label style={S.label}>Role (this shift)</label><input style={S.input} value={form.role||""} onChange={e => setForm(p=>({...p,role:e.target.value}))} placeholder="e.g. Floor, Bar" /></div>
            <div><label style={S.label}>Start Time</label><input style={S.input} type="time" value={form.start_time||""} onChange={e => setForm(p=>({...p,start_time:e.target.value}))} /></div>
            <div><label style={S.label}>End Time</label><input style={S.input} type="time" value={form.end_time||""} onChange={e => setForm(p=>({...p,end_time:e.target.value}))} /></div>
            <div><label style={S.label}>Break (minutes)</label><input style={S.input} type="number" value={form.break_minutes??30} onChange={e => setForm(p=>({...p,break_minutes:+e.target.value}))} /></div>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status||"scheduled"} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                {["scheduled","confirmed","completed","absent"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div style={{ gridColumn:"span 2", background:C.surface, borderRadius:8, padding:10, fontSize:13 }}>
              Shift duration: <strong>{calcHours(form.start_time,form.end_time,form.break_minutes).toFixed(2)} hours</strong>
              {(() => { const s = staff.find(x=>x.id===form.staff_id); return s ? <span style={{ marginLeft:12, color:"#64748b" }}>Est. cost: <strong>{fmt(calcHours(form.start_time,form.end_time,form.break_minutes)*s.hourly_rate)}</strong></span> : null; })()}
            </div>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Notes</label><textarea style={{ ...S.input, height:56 }} value={form.notes||""} onChange={e => setForm(p=>({...p,notes:e.target.value}))} /></div>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:20 }}>
            {form.id && <button style={S.btn("danger")} onClick={() => { delShift(form.id); setModal(null); }}>Delete Shift</button>}
            <div style={{ display:"flex", gap:10, marginLeft:"auto" }}>
              <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
              <button style={S.btn()} onClick={saveShift}>Save Shift</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── VAT RETURNS ─────────────────────────────────────────────────────────────

function VATReturns() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");           // list-view failures (load, delete) — top-of-page banner
  const [canWrite, setCanWrite] = useState(false);   // P4-CC18: fail closed — see canWriteVat()
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [calculating, setCalculating] = useState(false);
  const [calcError, setCalcError] = useState("");    // calculation failures — shown beside the figures
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");    // save/reopen failures — shown inside the modal
  const [importNote, setImportNote] = useState(null); // { imported, skipped } | null — one-time import summary

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setRows(await listVatReturns()); }
    catch (e) { setError(e.message || "Could not load VAT returns."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => { try { const ok = await canWriteVat(); if (alive) setCanWrite(ok); } catch { if (alive) setCanWrite(false); } })();
    return () => { alive = false; };
  }, []);

  // One-time import of legacy browser-only VAT returns (P4-CC18). Runs ONCE —
  // localStorage.vatReturns.<flag> below — and ONLY into a company with zero
  // real rows, so it can never overwrite anything already filed for real.
  // Every legacy row is imported as a DRAFT regardless of its old status: the
  // old localStorage schema never recorded a submission timestamp, and
  // vat_returns_submitted_at_chk requires one for any non-draft row —
  // inventing a fake "just submitted" timestamp would misrepresent history.
  // The figures are fully preserved either way; a real submission can be
  // redone properly through this screen. Any row the database rejects for
  // ANY reason (an overlapping period is the expected case, but not the only
  // possible one — e.g. a legacy record missing its dates) is skipped and
  // named, never silently dropped. localStorage.vatReturns is NEVER cleared
  // by this, regardless of outcome — it stays as a safety net.
  const importLegacyReturns = useCallback(async () => {
    const FLAG_KEY = STORAGE_PREFIX + "vatReturnsImported";
    try {
      if (localStorage.getItem(FLAG_KEY)) return;
      let legacy = [];
      try { legacy = JSON.parse(localStorage.getItem(STORAGE_PREFIX + "vatReturns") || "[]"); } catch { legacy = []; }
      if (!Array.isArray(legacy) || legacy.length === 0) { localStorage.setItem(FLAG_KEY, "1"); return; }
      const current = await listVatReturns();
      if (current.length > 0) { localStorage.setItem(FLAG_KEY, "1"); return; } // only ever import into an empty company

      let imported = 0;
      const skipped = [];
      for (const old of legacy) {
        const label = old.period_start && old.period_end
          ? `${old.period_start} to ${old.period_end}`
          : (old.period_start || old.period_end || `record ${imported + skipped.length + 1}`);
        // The status itself is downgraded to 'draft' (see the comment above),
        // but that fact a user recorded this return as filed must not simply
        // vanish — it's exactly what a six-year statutory record exists to
        // preserve. Record it in notes instead, ahead of whatever notes text
        // already existed, never in place of it.
        const oldStatusLabel = old.status && old.status !== "draft"
          ? (VAT_RETURN_STATUSES.find((s) => s.value === old.status)?.label || old.status)
          : null;
        const importPrefix = oldStatusLabel
          ? `Imported from browser storage on ${new Date().toLocaleDateString("en-IE")}. Status in the previous system: ${oldStatusLabel}. Re-file or update the status if this return was already submitted to Revenue.`
          : null;
        const notes = importPrefix ? `${importPrefix}${old.notes ? `\n\n${old.notes}` : ""}` : (old.notes || null);
        try {
          await createVatReturn({
            return_period: old.return_period === "monthly" ? "monthly" : "quarterly",
            period_start: old.period_start,
            period_end: old.period_end,
            status: "draft",   // see comment above — never imported as filed; the ORIGINAL status is preserved in notes, not discarded
            reference_number: old.reference_number || null,
            sales_total_ex_vat: old.sales_total_ex_vat ?? 0,
            sales_0_rate: old.sales_0_rate ?? 0,
            sales_9_rate: old.sales_9_rate ?? 0,
            sales_13_5_rate: old.sales_13_5_rate ?? 0,
            sales_23_rate: old.sales_23_rate ?? 0,
            total_vat_on_sales: old.total_vat_on_sales ?? 0,
            purchases_total_ex_vat: old.purchases_total_ex_vat ?? 0,
            total_vat_recoverable: old.total_vat_recoverable ?? 0,
            // vat_payable_t3/vat_repayable_t4 are generated columns now — the
            // database derives them from the two lines above; nothing to send.
            reverse_charge_amount: old.reverse_charge_amount ?? 0,
            notes,
            calculated_at: null,   // unknown — forces Recalculate before this row can be saved again
          });
          imported++;
        } catch (e) {
          skipped.push(`${label} (${e.message || "rejected"})`);
        }
      }
      localStorage.setItem(FLAG_KEY, "1");
      if (imported > 0 || skipped.length > 0) { setImportNote({ imported, skipped }); await load(); }
    } catch {
      // Best-effort convenience, not a requirement — on outright failure (e.g.
      // offline) the flag is deliberately NOT set, so it's retried later
      // rather than silently abandoned.
    }
  }, [load]);

  useEffect(() => { (async () => { await load(); await importLegacyReturns(); })(); }, [load, importLegacyReturns]);

  const isStale = form.status === "draft" && form.calculated_at
    && (Date.now() - new Date(form.calculated_at).getTime()) > 7 * 24 * 60 * 60 * 1000;
  const editableFigures = canWrite && form.status === "draft";   // dates + return_period + Recalculate
  const editableMeta = canWrite;                                  // status/reference/notes — permitted even when filed
  const canSave = !saving && (form.status === "draft" ? (!!form.calculated_at && !calculating) : true);

  const recalculate = async (start, end) => {
    const s = start ?? form.period_start, e = end ?? form.period_end;
    if (!s || !e) { setCalcError("Set both period dates first."); return; }
    setCalculating(true); setCalcError("");
    try {
      const calc = await calculateVatReturn(s, e);
      setForm((p) => ({ ...p, ...calc }));
    } catch (err) {
      // Figures are left exactly as they were — blank on a brand-new return
      // (nothing to fall back to), or the previous successful calculation on
      // an existing one. Never a plausible-looking number from a failure.
      setCalcError(err.message || "Could not calculate VAT figures. Figures are unchanged.");
    } finally {
      setCalculating(false);
    }
  };

  const onDateChange = (key) => (v) => {
    // Changing the period invalidates whatever figures are on screen for the
    // OLD period — clear calculated_at rather than leaving a number that no
    // longer matches the selected dates. Save stays disabled until Recalculate runs.
    setForm((p) => ({ ...p, [key]: v, calculated_at: null }));
    setCalcError("");
  };

  const openNew = async () => {
    const now = new Date();
    const qStart = localISO(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1));
    const qEnd = localISO(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0));
    setForm({ return_period: "quarterly", period_start: qStart, period_end: qEnd, status: "draft", reverse_charge_amount: 0, notes: "" });
    setCalcError(""); setSaveError("");
    setModal("edit");
    await recalculate(qStart, qEnd);
  };
  const openRow = (v) => { setForm({ ...v }); setCalcError(""); setSaveError(""); setModal("edit"); };

  const save = async () => {
    setSaving(true); setSaveError("");
    try {
      if (form.id) await updateVatReturn(form.id, form); else await createVatReturn(form);
      setModal(null);
      await load();
    } catch (e) { setSaveError(e.message || "Could not save VAT return."); }
    finally { setSaving(false); }
  };
  const del = async (v) => {
    if (!window.confirm(`Delete the VAT return for ${v.period_start} to ${v.period_end}?`)) return;
    try { await deleteVatReturn(v.id); await load(); } catch (e) { setError(e.message || "Could not delete VAT return."); }
  };
  const reopen = async () => {
    if (!window.confirm("Reopen this rejected return for correction? Its status will change to Draft.")) return;
    setSaving(true); setSaveError("");
    try { const updated = await updateVatReturn(form.id, { ...form, status: "draft" }); setForm(updated); await load(); }
    catch (e) { setSaveError(e.message || "Could not reopen this return."); }
    finally { setSaving(false); }
  };

  const VatRow = ({ label, value, sub, indent }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}`, paddingLeft: indent ? 16 : 0 }}>
      <span style={{ color: sub ? C.textMuted : C.text, fontSize: sub ? 12 : 13 }}>{label}</span>
      <strong style={{ fontSize: 13 }}>{fmt(value)}</strong>
    </div>
  );
  const readonlyField = (v, extra) => <div style={{ ...S.input, background: "transparent", color: C.textMuted, ...extra }}>{v ?? "—"}</div>;

  // T3 (payable) / T4 (repayable) — mirrors the DB's generated-column formula
  // (greatest(total_vat_on_sales - total_vat_recoverable, 0), and the inverse)
  // exactly, off the same two source figures. A SAVED row's real
  // vat_payable_t3/vat_repayable_t4 always equal this, so this is safe to use
  // uniformly rather than branching on whether the row has round-tripped
  // through the database yet — which matters for a NEW return: right after
  // Recalculate, calculated_at is set but the row hasn't been saved, so
  // v.vat_payable_t3 would still be undefined. Reading it directly there would
  // read as "0 payable" — the exact plausible-looking-zero this page has
  // already been bitten by once (the old unconditional fmt(vat_payable) bug).
  //
  // TWO COPIES OF ONE FORMULA — THEY MUST STAY IDENTICAL. The other copy is
  // vat_payable_t3/vat_repayable_t4 in supabase/migrations/20260807120000_vat_returns_t3_t4.sql
  // (the `generated always as (greatest(...))` column definitions). A change
  // to the formula in either place (rounding, which figures it's derived
  // from, the greatest()/floor-at-zero behaviour) requires the identical
  // change here, or this preview will silently disagree with what gets saved.
  const deriveT34 = (v) => {
    if (v.calculated_at == null) return null;
    const sales = Number(v.total_vat_on_sales) || 0;
    const recoverable = Number(v.total_vat_recoverable) || 0;
    return { t3: Math.max(sales - recoverable, 0), t4: Math.max(recoverable - sales, 0) };
  };

  return (
    <div>
      {importNote && (
        <div role="status" style={{ background: C.primaryDim, border: `1px solid ${C.primary}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div>
              <strong>Imported {importNote.imported} VAT return{importNote.imported === 1 ? "" : "s"} from this browser's saved data.</strong>
              {importNote.skipped.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  Skipped ({importNote.skipped.length}) — could not be imported:
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>{importNote.skipped.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              <div style={{ marginTop: 6, color: C.textMuted }}>Your original browser data was not deleted.</div>
            </div>
            <button style={S.btn("secondary")} onClick={() => setImportNote(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        {canWrite && <button style={S.btn()} onClick={openNew}>+ New VAT Return</button>}
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr><th style={S.th}>Period</th><th style={S.th}>Type</th><th style={S.th}>Sales (ex VAT)</th><th style={S.th}>VAT on Sales</th><th style={S.th}>VAT Recoverable</th><th style={S.th}>VAT Payable / Repayable</th><th style={S.th}>Status</th><th style={S.th}></th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={S.empty}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={S.empty}>
                <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No VAT returns yet</div>
                <div style={{ marginBottom: canWrite ? 12 : 0 }}>Figures are calculated from your live invoices and expenses.</div>
                {canWrite && <button style={S.btn()} onClick={openNew}>+ New VAT Return</button>}
              </td></tr>
            ) : rows.map((v) => {
              const t34 = deriveT34(v);
              return (
                <tr key={v.id} style={{ cursor: "pointer" }} onClick={() => openRow(v)}>
                  <td style={S.td}><strong>{v.period_start}</strong><div style={{ fontSize: 11, color: C.textMuted }}>to {v.period_end}</div></td>
                  <td style={S.td}>{v.return_period}</td>
                  <td style={S.td}>{fmt(v.sales_total_ex_vat)}</td>
                  <td style={S.td}>{fmt(v.total_vat_on_sales)}</td>
                  <td style={S.td}>{fmt(v.total_vat_recoverable)}</td>
                  <td style={S.td}>
                    {!t34
                      ? <span style={{ color: C.textMuted }}>—</span>
                      : t34.t3 > 0
                        ? <strong style={{ color: C.danger }}>{fmt(t34.t3)}</strong>
                        : t34.t4 > 0
                          ? <strong style={{ color: C.success }}>{fmt(t34.t4)} repayable</strong>
                          : <strong style={{ color: C.success }}>{fmt(0)}</strong>}
                  </td>
                  <td style={S.td}><Badge status={v.status} /></td>
                  <td style={S.td} onClick={(e) => e.stopPropagation()}>
                    {canWrite && v.status === "draft" && <button style={S.btn("ghost")} aria-label="Delete VAT return" onClick={() => del(v)}>🗑</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal === "edit" && (
        <Modal title={form.id ? `VAT Return — ${form.period_start} to ${form.period_end}` : "New VAT Return"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div><label style={S.label}>Period Start *</label>
              {editableFigures ? <input style={S.input} type="date" value={form.period_start || ""} onChange={(e) => onDateChange("period_start")(e.target.value)} /> : readonlyField(form.period_start)}
            </div>
            <div><label style={S.label}>Period End *</label>
              {editableFigures ? <input style={S.input} type="date" value={form.period_end || ""} onChange={(e) => onDateChange("period_end")(e.target.value)} /> : readonlyField(form.period_end)}
            </div>
            <div><label style={S.label}>Return Period</label>
              {editableFigures ? (
                <select style={S.input} value={form.return_period || "quarterly"} onChange={(e) => setForm((p) => ({ ...p, return_period: e.target.value }))}>
                  {RETURN_PERIODS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              ) : readonlyField(RETURN_PERIODS.find((r) => r.value === form.return_period)?.label || form.return_period)}
            </div>
            <div><label style={S.label}>Status</label>
              {editableMeta ? (
                <select style={S.input} value={form.status || "draft"} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                  {(form.status === "draft" ? VAT_RETURN_STATUSES : VAT_RETURN_STATUSES.filter((s) => s.value !== "draft")).map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              ) : readonlyField(<Badge status={form.status} />)}
            </div>
            {form.status !== "draft" && (
              <div><label style={S.label}>Reference Number</label>
                {editableMeta ? <input style={S.input} value={form.reference_number || ""} onChange={(e) => setForm((p) => ({ ...p, reference_number: e.target.value }))} /> : readonlyField(form.reference_number)}
              </div>
            )}
          </div>

          <div style={{ background: C.warningDim, border: `2px solid ${C.warning}`, borderRadius: 8, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: C.text }}>
            <div style={{ fontWeight: 700, color: C.warning, marginBottom: 4 }}>⚠ Review before filing</div>
            <div>These figures are computed from your live invoices and expenses, and are only as complete and accurate as the records entered. Check them against your own accounts before filing with Revenue.</div>
            <div style={{ marginTop: 4 }}>Reverse-charge / import VAT and the annual Return of Trading Details (RTD) are <strong>not</strong> modelled here.</div>
          </div>

          {form.status === "rejected" && canWrite && (
            <div style={{ marginBottom: 16 }}>
              <button style={S.btn("secondary")} onClick={reopen} disabled={saving}>↺ Reopen for correction</button>
            </div>
          )}

          {calcError && (
            <div role="alert" style={{ color: C.danger, background: C.dangerDim, border: `1px solid ${C.danger}`, borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 13, fontWeight: 600 }}>⚠ {calcError}</div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div style={{ background: C.surface2, borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Sales (from invoices)</div>
              {calculating ? <div style={{ color: C.textMuted, fontSize: 13, padding: "8px 0" }}>Calculating…</div>
                : form.calculated_at == null ? <div style={{ color: C.textMuted, fontSize: 13, padding: "8px 0" }}>Not yet calculated for this period.</div>
                : (<>
                  <VatRow label="Total Sales (ex VAT)" value={form.sales_total_ex_vat} />
                  <VatRow label="Sales at 0%" value={form.sales_0_rate} sub indent />
                  <VatRow label="Sales at 9%" value={form.sales_9_rate} sub indent />
                  <VatRow label="Sales at 13.5%" value={form.sales_13_5_rate} sub indent />
                  <VatRow label="Sales at 23%" value={form.sales_23_rate} sub indent />
                  <VatRow label="Total VAT on Sales" value={form.total_vat_on_sales} />
                </>)}
            </div>
            <div style={{ background: C.surface2, borderRadius: 8, padding: 14 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: C.textDim, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>Purchases (from expenses)</div>
              {calculating ? <div style={{ color: C.textMuted, fontSize: 13, padding: "8px 0" }}>Calculating…</div>
                : form.calculated_at == null ? <div style={{ color: C.textMuted, fontSize: 13, padding: "8px 0" }}>Not yet calculated for this period.</div>
                : (<>
                  <VatRow label="Total Purchases (ex VAT)" value={form.purchases_total_ex_vat} />
                  <VatRow label="VAT Recoverable" value={form.total_vat_recoverable} />
                </>)}
              <div style={{ marginTop: 12 }}>
                <label style={S.label}>Reverse charge (not yet included in this calculation)</label>
                <input style={{ ...S.input, opacity: 0.6, cursor: "not-allowed" }} type="number" value={form.reverse_charge_amount || 0} disabled />
              </div>
            </div>
          </div>

          {editableFigures && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
              <button style={S.btn("secondary")} onClick={() => recalculate()} disabled={calculating}>{calculating ? "Calculating…" : "↻ Recalculate"}</button>
            </div>
          )}
          {form.calculated_at != null && (
            <div style={{ fontSize: 11, color: C.textDim, marginBottom: 8 }}>Figures calculated {new Date(form.calculated_at).toLocaleString("en-IE")}</div>
          )}
          {isStale && (
            <div style={{ fontSize: 12, color: C.warning, background: C.warningDim, border: `1px solid ${C.warning}`, borderRadius: 6, padding: "6px 10px", marginBottom: 12, fontWeight: 600 }}>
              ⚠ These figures are more than a week old. Recalculate before filing.
            </div>
          )}

          {/* Never render this as €0.00 before a calculation has actually run — that
              is exactly the plausible-looking-zero this whole package exists to stop.
              T3 (payable) and T4 (repayable) are Revenue's own two boxes — never
              collapse them into one signed number, and never show a negative
              against a "payable" label (see 20260807120000_vat_returns_t3_t4.sql). */}
          {(() => {
            const t34 = deriveT34(form);
            const isRepayable = !!t34 && t34.t4 > 0;
            return (
              <div style={{ background: !t34 ? C.surface2 : isRepayable ? C.successDim : t34.t3 > 0 ? C.dangerDim : C.successDim, borderRadius: 8, padding: 16, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 700, fontSize: 15 }}>{isRepayable ? "VAT Repayable to You" : "VAT Payable to Revenue"}</span>
                <span style={{ fontWeight: 800, fontSize: 22, color: !t34 ? C.textMuted : isRepayable ? C.success : t34.t3 > 0 ? C.danger : C.success }}>
                  {!t34 ? "—" : fmt(isRepayable ? t34.t4 : t34.t3)}
                </span>
              </div>
            );
          })()}

          <div><label style={S.label}>Notes</label>
            {editableMeta ? <textarea style={{ ...S.input, height: 56 }} value={form.notes || ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} /> : readonlyField(form.notes, { height: 56, whiteSpace: "pre-wrap" })}
          </div>

          {saveError && <div role="alert" style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }}>⚠ {saveError}</div>}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            {canWrite && <button style={{ ...S.btn(), opacity: canSave ? 1 : 0.5 }} onClick={save} disabled={!canSave}>{saving ? "Saving…" : "Save Return"}</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── PROJECTS ────────────────────────────────────────────────────────────────

function Projects({ projects, setProjects, customers }) {
  const [modal, setModal] = useState(null);
  const [form, setForm]   = useState({});
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = projects
    .filter(p => filterStatus === "all" || p.status === filterStatus)
    .filter(p => `${p.name} ${p.customer_name||""}`.toLowerCase().includes(search.toLowerCase()));

  const totalBudget = filtered.reduce((s,p) => s+(p.budget||0), 0);
  const totalSpent  = filtered.reduce((s,p) => s+(p.spent||0), 0);

  const save = () => {
    if (!form.id) setProjects(p => [{ ...form, id:uid(), spent:0, progress_percentage:0 }, ...p]);
    else setProjects(p => p.map(x => x.id === form.id ? form : x));
    setModal(null);
  };
  const del = (id) => { if (window.confirm("Delete project?")) setProjects(p => p.filter(x => x.id !== id)); };

  const STATUS_OPTS = ["planning","in_progress","on_hold","completed","cancelled"];

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:16, gap:10 }}>
        <div style={{ display:"flex", gap:10 }}>
          <input style={S.searchBar} placeholder="Search projects..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...S.input, width:"auto" }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">All statuses</option>
            {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
          </select>
        </div>
        <button style={S.btn()} onClick={() => { setForm({ status:"planning", progress_percentage:0, spent:0 }); setModal("edit"); }}>+ New Project</button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16, marginBottom:24 }}>
        <div style={S.statCard(C.primary)}><div style={S.statLabel}>Active Projects</div><div style={S.statValue}>{projects.filter(p=>p.status==="in_progress").length}</div></div>
        <div style={S.statCard(C.success)}><div style={S.statLabel}>Total Budget</div><div style={S.statValue}>{fmt(totalBudget)}</div></div>
        <div style={S.statCard(C.warning)}><div style={S.statLabel}>Total Spent</div><div style={S.statValue}>{fmt(totalSpent)}</div></div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:16 }}>
        {filtered.length === 0 && <div style={{ ...S.card, color:"#94a3b8", textAlign:"center", padding:32 }}>No projects yet.</div>}
        {filtered.map(p => {
          const pct = Math.min(100, p.progress_percentage || 0);
          const overBudget = (p.spent||0) > (p.budget||0);
          return (
            <div key={p.id} style={{ ...S.card, cursor:"pointer" }} onClick={() => { setForm({...p}); setModal("edit"); }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15 }}>{p.name}</div>
                  <div style={{ fontSize:12, color:"#64748b" }}>{p.customer_name}</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <Badge status={p.status} />
                  <button style={{ ...S.btn("ghost"), padding:"2px 6px" }} onClick={e => { e.stopPropagation(); del(p.id); }}>🗑</button>
                </div>
              </div>
              {p.description && <div style={{ fontSize:12, color:"#64748b", marginBottom:10 }}>{p.description}</div>}
              <div style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                  <span>Progress</span><strong>{pct}%</strong>
                </div>
                <div style={{ background:C.border, borderRadius:4, height:6 }}>
                  <div style={{ background:C.primary, borderRadius:4, height:6, width:`${pct}%`, transition:"width 0.3s" }} />
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:12 }}>
                <div><span style={{ color:"#64748b" }}>Budget: </span><strong>{fmt(p.budget)}</strong></div>
                <div><span style={{ color:"#64748b" }}>Spent: </span><strong style={{ color:overBudget?C.danger:undefined }}>{fmt(p.spent)}</strong></div>
                {p.start_date && <div><span style={{ color:"#64748b" }}>Start: </span>{p.start_date}</div>}
                {p.end_date   && <div><span style={{ color:"#64748b" }}>End: </span>{p.end_date}</div>}
              </div>
              {overBudget && <div style={{ fontSize:11, color:C.danger, fontWeight:700, marginTop:8 }}>⚠ Over budget by {fmt((p.spent||0)-(p.budget||0))}</div>}
            </div>
          );
        })}
      </div>

      {modal === "edit" && (
        <Modal title={form.id ? "Edit Project" : "New Project"} onClose={() => setModal(null)}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Project Name *</label><input style={S.input} value={form.name||""} onChange={e => setForm(p=>({...p,name:e.target.value}))} /></div>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Customer</label>
              <select style={S.input} value={form.customer_id||""} onChange={e => { const c=customers.find(x=>x.id===e.target.value); setForm(p=>({...p,customer_id:c?.id||"",customer_name:c?.name||""})); }}>
                <option value="">None</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.name} — {c.company}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Status</label>
              <select style={S.input} value={form.status||"planning"} onChange={e => setForm(p=>({...p,status:e.target.value}))}>
                {STATUS_OPTS.map(s => <option key={s} value={s}>{s.replace(/_/g," ")}</option>)}
              </select>
            </div>
            <div><label style={S.label}>Progress %</label><input style={S.input} type="number" min={0} max={100} value={form.progress_percentage||0} onChange={e => setForm(p=>({...p,progress_percentage:+e.target.value}))} /></div>
            <div><label style={S.label}>Budget (€)</label><input style={S.input} type="number" value={form.budget||""} onChange={e => setForm(p=>({...p,budget:+e.target.value}))} /></div>
            <div><label style={S.label}>Spent (€)</label><input style={S.input} type="number" value={form.spent||0} onChange={e => setForm(p=>({...p,spent:+e.target.value}))} /></div>
            <div><label style={S.label}>Start Date</label><input style={S.input} type="date" value={form.start_date||""} onChange={e => setForm(p=>({...p,start_date:e.target.value}))} /></div>
            <div><label style={S.label}>End Date</label><input style={S.input} type="date" value={form.end_date||""} onChange={e => setForm(p=>({...p,end_date:e.target.value}))} /></div>
            <div style={{ gridColumn:"span 2" }}><label style={S.label}>Description</label><textarea style={{ ...S.input, height:72 }} value={form.description||""} onChange={e => setForm(p=>({...p,description:e.target.value}))} /></div>
          </div>
          <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
            <button style={S.btn("secondary")} onClick={() => setModal(null)}>Cancel</button>
            <button style={S.btn()} onClick={save}>Save Project</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── AUTH ────────────────────────────────────────────────────────────────────

// Pages a limited (non-owner/admin) user is allowed to see.
// (Role-gated STAFF_PAGES removed — all completed modules are visible by default;
//  intentional RBAC returns in Phase 8.)

// (The old mock Login was replaced by src/auth/AuthScreen.jsx + Supabase Auth.)

// ─── MAIN APP ────────────────────────────────────────────────────────────────

export default function MsquaredApp() {
  // ── LT-1 — THE URL IS THE STATE ──────────────────────────────────────────
  // `page` was useState("dashboard"): the URL never moved, so the back button
  // left the app, a reload lost the screen, and no link could name a record.
  // It now derives from the path. Every navigation goes through navigate(),
  // which is the ONLY writer — nothing calls setPage any more because there is
  // no setPage.
  const { page, param, action } = useRoute();
  // Real auth (Supabase). Derives a session-shaped object so downstream UI is unchanged.
  const { loading: authLoading, user, role: companyRole, signOut, billing, companyName } = useAuth();
  // Legacy localStorage customers — still read by Dashboard/Invoices/Orders/etc.
  // The Customers PAGE itself now uses the Supabase-backed CustomerManager (Phase 2).
  // Phase 3: migrate these consumers onto the Supabase customer source.
  const [customers] = usePersistentState("customers", SEED_CUSTOMERS);
  // Live products from Supabase — single load point, shared with the Invoices/Orders/
  // Quotations pickers and Inventory. All access goes through the products data layer
  // (no direct Supabase here, no localStorage product storage). (P3-WP4)
  const [products, setProducts] = useState([]);
  const loadProducts = useCallback(async () => {
    try { const { data } = await getProducts({ pageSize: 1000, sortBy: "name" }); setProducts(data); }
    catch { /* pickers degrade to empty; the Products page surfaces real load errors */ }
  }, []);
  const [orders, setOrders] = usePersistentState("orders", SEED_ORDERS);
  const [tasks, setTasks] = usePersistentState("tasks", []);
  const [bankAccounts, setBankAccounts]   = usePersistentState("bankAccounts", []);
  const [transactions, setTransactions]   = usePersistentState("transactions", []);
  const [calEvents, setCalEvents]         = usePersistentState("calEvents", []);
  // P4-CC26d-0: DEFAULT_COA is deleted. The KEY is deliberately left in place
  // and unread by the Chart of Accounts tab, which now reads public.accounts.
  // Anything a user added lives only here, so it is preserved rather than
  // cleared.
  const [accounts, setAccounts]           = usePersistentState("accounts", []);
  // journals: state REMOVED (P4-CC26d-0-fix). Nothing reads or writes the array
  // now that the Journal Entries tab is a placeholder, and an unread useState is
  // how a dead feature quietly stays wired up. THE localStorage KEY IS NOT
  // DELETED — usePersistentState only ever reads and writes, never removes, so
  // any entries a user typed before today survive untouched in msq_journals for
  // CC26d-1 to decide about. Deleting a user's data to tidy up a refactor would
  // be the wrong trade in both directions: it is theirs, and it is bookkeeping.
  const [payroll, setPayroll]             = usePersistentState("payroll", []);
  const [staff, setStaff]                 = usePersistentState("staff", []);
  const [shifts, setShifts]               = usePersistentState("shifts", []);
  // vatReturns: REMOVED (P4-CC18) — VATReturns is now self-loading from
  // Supabase via listVatReturns(); localStorage.vatReturns (if any) is read
  // once, directly, by VATReturns' own one-time import — see its own comment.
  const [projects, setProjects]           = usePersistentState("projects", []);

  // ── LT-1 — HIDDEN, NOT DELETED. THIS IS THE LINE THAT PROVES IT ──────────
  // The seven preview screens and every localStorage key behind them are kept
  // exactly as they were. Nothing renders them today — the six route to
  // ComingSoon and `orders` is gone from the nav — so without this reference
  // eslint's no-unused-vars would fail the CI build, and the obvious way to
  // silence that is to DELETE the components and their state. That is the one
  // outcome this package must not produce: a customer who typed a month of
  // rota or petty-cash entries into a preview screen still has that data in
  // their browser, and it stays there. Resurrecting a screen is one case in
  // renderPage() and one entry in NAV.
  //
  // BrowserOnlyBanner is kept for the same reason. Nothing uses it now — the
  // six screens it labelled are behind ComingSoon — but it is the finished,
  // reviewed wording for "this saves to one browser only", and the next
  // preview screen should reuse it rather than re-derive it.
  //
  // ⚠️ `products` IS NOW FETCHED AND READ BY NOTHING. loadProducts() fed the
  // Orders screen's product picker and has no other consumer since Orders
  // left the nav. It still runs on dashboard/invoices/salesorders/quotations/
  // inventory. Left in place deliberately rather than removed inside a
  // hide-the-screens package — reported for a follow-up, not decided here.
  // eslint-disable-next-line no-unused-vars
  const KEPT_PREVIEW_SCREENS = { Orders, Bank, Payroll, StaffRota, CalendarPage, Projects, AccountingTasks, BrowserOnlyBanner };
  // eslint-disable-next-line no-unused-vars
  const KEPT_PREVIEW_STATE = {
    customers, products,
    orders, setOrders, tasks, setTasks,
    bankAccounts, setBankAccounts, transactions, setTransactions,
    calEvents, setCalEvents, payroll, setPayroll,
    staff, setStaff, shifts, setShifts, projects, setProjects,
  };

  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);
  // LT-1: the "Coming soon" section starts collapsed. Deliberately NOT
  // persisted — it should read as shut on every visit, because a section a
  // customer left open once is a section that looks like part of the product.
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  // Quick Actions: navigate to a module and signal it to open its "new" modal.
  const [pendingNew, setPendingNew] = useState(null);
  // Dashboard → open a specific existing invoice in the editor.
  const [pendingOpenInvoice, setPendingOpenInvoice] = useState(null);
  // Quotation conversion → jump to the created sales order.
  const [pendingOpenSO, setPendingOpenSO] = useState(null);
  const [pendingOpenCreditNote, setPendingOpenCreditNote] = useState(null);
  const [pendingStatementCustomer, setPendingStatementCustomer] = useState(null);
  const [pendingOpenCustomer, setPendingOpenCustomer] = useState(null);

  // ── LT-1 — THE ROUTE DRIVES THE pending* MECHANISMS ──────────────────────
  // These were set by the click handlers, which meant a deep link or a reload
  // arrived on the right PAGE with no record open — the link half-worked, which
  // is worse than not working, because nothing tells the user what they missed.
  // Seeding them from the route makes a pasted /customers/<id>, a reload of it,
  // and a click from the dashboard all follow the identical path.
  //
  // The managers still CLEAR their own flag (onAutoNew / onOpenHandled), and
  // that is deliberate: the flag is a one-shot instruction, not a mirror of the
  // URL. The URL keeps naming the record — a reload reopens it — while the
  // cleared flag stops the editor reopening every time the component re-renders.
  useEffect(() => {
    if (action === "new") { setPendingNew(page); return; }
    if (!param) return;
    if (page === "invoices") setPendingOpenInvoice(param);
    else if (page === "creditnotes") setPendingOpenCreditNote(param);
    else if (page === "statements") setPendingStatementCustomer(param);
    else if (page === "customers") setPendingOpenCustomer(param);
    else if (page === "salesorders") setPendingOpenSO(param);
  }, [page, param, action]);

  // ── LT-1 — the tab title names the screen ────────────────────────────────
  // Runs for signed-out visitors too: someone who lands on /invoices from a
  // bookmark sees "Invoices — M²" while they sign in, rather than a title that
  // contradicts the page they asked for.
  useEffect(() => {
    const label = NAV.flatMap(n => n.items).find(i => i.id === page)?.label;
    document.title = label ? `${label} — M²` : "M²";
  }, [page]);

  // "Saved" toast — shown briefly whenever a slice is persisted to localStorage.
  const [savedToast, setSavedToast] = useState(false);
  const toastReady = useRef(false);
  useEffect(() => {
    // Ignore the burst of writes that fire during initial mount/hydration.
    const readyId = setTimeout(() => { toastReady.current = true; }, 600);
    let hideId;
    const onSaved = () => {
      if (!toastReady.current) return;
      setSavedToast(true);
      clearTimeout(hideId);
      hideId = setTimeout(() => setSavedToast(false), 1500);
    };
    window.addEventListener("msq:saved", onSaved);
    return () => { window.removeEventListener("msq:saved", onSaved); clearTimeout(readyId); clearTimeout(hideId); };
  }, []);

  // Load live products on auth and when entering a page that uses them (P3-WP4).
  useEffect(() => {
    if (user && ["dashboard", "invoices", "salesorders", "quotations", "inventory"].includes(page)) loadProducts();
  }, [user, page, loadProducts]);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const logout = () => {
    signOut();
    setNavOpen(false);
    // replace, not push: the back button must not walk a signed-out user back
    // into the screen they just left.
    navigate("/", { replace: true });
  };

  // LT-4a. MUST be called here, above the auth gate below — `if (!user) return`
  // is an early return, so a hook placed after it would run on some renders and
  // not others and React would tear the hook order apart. eslint caught exactly
  // that on the first attempt. It is safe this high up: useOnboarding() resolves
  // to { show: false } while there is no companyId, so nothing renders for a
  // signed-out visitor regardless of where it is called.
  const onboarding = useOnboarding();

  // All hooks have run above; safe to gate the rest of the UI on auth.
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.bg, color: "#64748b", fontFamily: "'Inter', system-ui, sans-serif", fontSize: 14 }}>
        Loading…
      </div>
    );
  }
  if (!user) return <AuthScreen />;

  // Session-shaped object derived from Supabase auth + company membership.
  const session = {
    name: user.user_metadata?.full_name || user.email,
    email: user.email,
    role: companyRole, // owner | admin | accountant | driver | sales | employee | null
  };

  // Config-driven, role-based navigation — GENUINE RBAC, no hardcoded bypass.
  // The sidebar is rendered from the NAV config. Owner/admin have full access; any NAV
  // item may restrict itself to specific roles via an optional `roles: [...]` array in
  // the config. Items with no `roles` are visible to every authenticated user, so a
  // completed module appears automatically the moment it's registered in NAV — and a
  // genuine owner naturally sees the whole app because role === "owner". Future
  // restrictions belong in the NAV config, never in a hardcoded flag here.
  const role = session.role; // from the user's real company membership
  const isFullAccess = role === "owner" || role === "admin";
  const itemAllowed = (item) => isFullAccess || !item.roles || (Array.isArray(item.roles) && item.roles.includes(role));
  const visibleNav = NAV
    .map(sec => ({ ...sec, items: sec.items.filter(itemAllowed) }))
    .filter(sec => sec.items.length > 0);
  const navItem = NAV.flatMap(s => s.items).find(i => i.id === page);
  // Fail CLOSED: an id with no NAV entry falls back to the dashboard rather than
  // being let through unchecked. The old gate skipped the role check entirely
  // for any page absent from NAV. Inert while every renderPage case is also a NAV
  // item (29 for 29 on 3 Nov), but a page added to renderPage without a NAV entry
  // would have been reachable by URL by every role, with nothing to say so.
  const allowedPage = navItem && itemAllowed(navItem) ? page : "dashboard";
  const pageTitle = NAV.flatMap(n => n.items).find(i => i.id === allowedPage)?.label || "Dashboard";

  // On mobile the sidebar is a fixed slide-in drawer; on desktop it's in-flow.
  const sidebarStyle = isMobile
    ? { ...S.sidebar, position: "fixed", top: 0, left: 0, height: "100%", zIndex: 200, transform: navOpen ? "translateX(0)" : "translateX(-100%)", transition: "transform 0.25s ease", boxShadow: navOpen ? "2px 0 16px rgba(0,0,0,0.3)" : "none" }
    : S.sidebar;

  // Every helper now writes the URL and nothing else. The pending* state that
  // used to be set here is seeded by the route effect above, so a DEEP LINK and
  // a RELOAD take exactly the same path through the app as a click does —
  // which is the property that makes a pasted link work at all.
  const go = (id) => { navigate("/" + id); setNavOpen(false); };
  const quickAction = (id) => { navigate("/new/" + id); setNavOpen(false); };
  const openInvoice = (id) => { navigate("/invoices/" + id); setNavOpen(false); };
  const openCreditNote = (id) => { navigate("/creditnotes/" + id); setNavOpen(false); };
  const openStatement = (customerId) => { navigate("/statements/" + customerId); setNavOpen(false); };
  const openCustomer = (id) => { navigate("/customers/" + id); setNavOpen(false); };

  // Wipe persisted data and reload back to the original sample data.
  const resetData = () => {
    if (!window.confirm("Reset all data back to the original sample data?\n\nThis permanently deletes everything you've added or changed.")) return;
    try {
      Object.keys(localStorage).filter(k => k.startsWith(STORAGE_PREFIX)).forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
    window.location.reload();
  };

  const renderPage = () => {
    switch (allowedPage) {
      case "dashboard": return <><DemoDataBanner onLoaded={() => window.location.reload()} /><Dashboard onQuickAction={quickAction} onOpenInvoice={openInvoice} /></>;
      case "customers": return <CustomerManager autoNew={pendingNew === "customers"} onAutoNew={() => setPendingNew(null)} onOpenStatement={openStatement} openId={pendingOpenCustomer} onOpenHandled={() => setPendingOpenCustomer(null)} />;
      case "intelligence": return <IntelligenceManager onOpenCustomer={openCustomer} />;
      case "statements": return <StatementManager openCustomerId={pendingStatementCustomer} onOpenHandled={() => setPendingStatementCustomer(null)} />;
      case "invoices": return <InvoiceManager autoNew={pendingNew === "invoices"} onAutoNew={() => setPendingNew(null)} openId={pendingOpenInvoice} onOpenHandled={() => setPendingOpenInvoice(null)} onOpenCreditNote={openCreditNote} />;
      case "creditnotes": return <CreditNoteManager openId={pendingOpenCreditNote} onOpenHandled={() => setPendingOpenCreditNote(null)} />;
      case "salesorders": return <SalesOrderManager autoNew={pendingNew === "salesorders"} onAutoNew={() => setPendingNew(null)} openId={pendingOpenSO} onOpenHandled={() => setPendingOpenSO(null)} onOpenInvoice={openInvoice} />;
      case "deliverynotes": return <DeliveryNoteManager autoNew={pendingNew === "deliverynotes"} onAutoNew={() => setPendingNew(null)} />;
      case "products": return <ProductManager autoNew={pendingNew === "products"} onAutoNew={() => setPendingNew(null)} />;
      case "pricelists": return <PriceListManager />;
      case "customergroups": return <CustomerGroupManager />;
      case "suppliers": return <SupplierManager autoNew={pendingNew === "suppliers"} onAutoNew={() => setPendingNew(null)} />;
      case "purchaseorders": return <PurchaseOrderManager autoNew={pendingNew === "purchaseorders"} onAutoNew={() => setPendingNew(null)} />;
      case "grn": return <GRNManager autoNew={pendingNew === "grn"} onAutoNew={() => setPendingNew(null)} />;
      case "quotations": return <QuotationManager autoNew={pendingNew === "quotations"} onAutoNew={() => setPendingNew(null)} onOpenSalesOrder={(id) => { navigate("/salesorders/" + id); setNavOpen(false); }} />;
      case "inventory": return <InventoryManager autoNew={pendingNew === "inventory"} onAutoNew={() => setPendingNew(null)} />;
      case "expenses": return <ExpenseManager autoNew={pendingNew === "expenses"} onAutoNew={() => setPendingNew(null)} />;
      case "vat": return <VATReturns />;
      // No BrowserOnlyBanner (P4-CC26d-0-fix3) — see the component's own comment.
      case "accounts": return <Accounts accounts={accounts} setAccounts={setAccounts} />;
      // LT-1 — the six hidden previews. ONE case, from the same list the nav
      // renders, so a screen cannot appear in "Coming soon" and render blank.
      case "bank": case "payroll": case "staff":
      case "calendar": case "projects": case "acctasks":
        return <ComingSoon page={allowedPage} onBack={() => go("dashboard")} />;
      case "billing": return <BillingScreen />;
      case "settings": return <BusinessSettings />;
      case "invoicetemplates": return <InvoiceTemplateManager />;
      case "team": return <TeamMembers />;
      default: return null;
    }
  };

  return (
    <div style={S.app}>
      {/* "Saved" toast */}
      <div style={{
        position: "fixed", bottom: 20, right: 20, zIndex: 300,
        background: C.primary, color: C.bg, padding: "10px 16px", borderRadius: 8,
        fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 8,
        boxShadow: "0 6px 20px rgba(0,182,214,0.35)", pointerEvents: "none",
        opacity: savedToast ? 1 : 0,
        transform: savedToast ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.25s ease, transform 0.25s ease",
      }}>
        <span style={{ color: C.success, fontSize: 15 }}>✓</span> Saved
      </div>

      <AtlasAssistant />

      {/* Mobile drawer backdrop */}
      {isMobile && navOpen && (
        <div onClick={() => setNavOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 199 }} />
      )}

      <div style={sidebarStyle}>
        <div style={S.logo}>
          <img src={`${process.env.PUBLIC_URL}/logo-msquared.png`} alt="Msquared" style={S.logoImg} />
          <div style={S.logoSub}>Business Platform</div>
        </div>
        <nav style={S.nav}>
          {visibleNav.map(section => {
            // LT-1: a `collapsed` section starts shut and renders greyed. The
            // items are NOT hidden from the DOM when open — collapsing is a
            // disclosure control, not a permission — and the whole section
            // stays reachable by keyboard because the header is a real button.
            const isComingSoon = !!section.collapsed;
            const open = isComingSoon ? comingSoonOpen : true;
            return (
              <div key={section.section} style={isComingSoon ? { opacity: 0.55 } : undefined}>
                {isComingSoon ? (
                  <button
                    type="button"
                    onClick={() => setComingSoonOpen(o => !o)}
                    aria-expanded={open}
                    style={{ ...S.navSection, display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", cursor: "pointer", textAlign: "left", font: "inherit", color: "inherit" }}
                  >
                    <span aria-hidden="true" style={{ display: "inline-block", transition: "transform 0.15s ease", transform: open ? "rotate(90deg)" : "none" }}>›</span>
                    <span>{section.section}</span>
                  </button>
                ) : (
                  <div style={S.navSection}>{section.section}</div>
                )}
                {open && section.items.map(item => (
                  <div key={item.id} style={S.navItem(allowedPage === item.id)} onClick={() => go(item.id)}>
                    <NavIcon id={item.id} size={18} />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </nav>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${C.border}`, color: C.textDim, fontSize: 11 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: C.primary, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
              {session.name.charAt(0).toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: C.white, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.name}</div>
              <div style={{ color: "#64748b", fontSize: 11, textTransform: "capitalize" }}>{session.role}</div>
            </div>
          </div>
          <button onClick={logout} title="Sign out"
            style={{ width: "100%", padding: "7px 10px", background: "transparent", border: "1px solid #334155", borderRadius: 6, color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            ⎋ Log out
          </button>
          {/* P4-CC17: dev-only. isFullAccess (owner/admin) gated nobody in practice — a beta
              customer IS an owner. This wipes real, unbacked-up browser data behind a single
              window.confirm; it must not exist in a build a customer can ever open. resetData()
              itself stays — it's still wanted for local development. */}
          {isFullAccess && process.env.NODE_ENV === "development" && (
            <button onClick={resetData} title="Clear saved data and restore the original sample data (development only)"
              style={{ width: "100%", marginTop: 8, padding: "7px 10px", background: "transparent", border: "1px solid #334155", borderRadius: 6, color: "#94a3b8", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              ↺ Reset data
            </button>
          )}
        </div>
      </div>

      <div style={S.main}>
        <div style={S.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            {isMobile && (
              <button onClick={() => setNavOpen(o => !o)} aria-label="Toggle menu"
                style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 18, lineHeight: 1, padding: "4px 10px", cursor: "pointer", color: C.text }}>
                ☰
              </button>
            )}
            <div style={S.pageTitle}>{pageTitle}</div>
          </div>
          {!isMobile && (
            <div style={{ color: "#64748b", fontSize: 13 }}>Msquared · {new Date().toLocaleDateString("en-IE", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
          )}
        </div>
        <div style={{ ...S.content, padding: isMobile ? 12 : 24 }}>
          {/* LT-2 — above EVERY page, not just the dashboard. A customer who
              lives on the Invoices screen must see the trial ending there.
              Suppressed on /billing itself, where the page says it better and
              the banner would only repeat a Subscribe button six inches from
              another one. */}
          {/* OCT-H2 — the DEMO DATA label, app-wide like the two billing banners.
              NOT in the dashboard-only slot where DemoDataBanner renders: a label
              that appears on one screen misses every other screenshot. It
              self-gates on demoStatus().seeded, so it costs nothing on a real
              workspace. */}
          <DemoModeBanner />
          {allowedPage !== "billing" && billing?.state === "trialing" && (
            <TrialBanner daysLeft={billing.daysLeft} onSubscribe={() => go("billing")} />
          )}
          {allowedPage !== "billing" && billing?.state === "read_only" && (
            <ReadOnlyBanner onSubscribe={() => go("billing")} />
          )}
          {renderPage()}
        </div>
      </div>

      {/* LT-4a — the onboarding wizard. Last child of the authenticated root, so
          it paints over the whole shell and never remounts on navigation. It is
          below the `if (!user)` gate above, so it cannot appear on the login
          screen. Whether it renders at all is entirely useOnboarding()'s
          decision: business_settings row missing, owner/admin, and not
          billing-locked. */}
      {onboarding.show && (
        <OnboardingWizard
          companyName={companyName}
          onComplete={onboarding.complete}
          onSkip={onboarding.skip}
          onDismiss={onboarding.dismiss}
        />
      )}
    </div>
  );
}
